/**
 * Copyright (c) 2024-2026 Creative Agentics Limited. All rights reserved.
 * Proprietary and confidential. Unauthorised use, reproduction or distribution
 * of this software, in whole or in part, is strictly prohibited.
 * See LICENSE.txt for full terms.
 */

/**
 * DECISION REVIEW REPORT — a faithful copy of the Sage Jaybee review report
 * (JaybeeReview.jsx v4_5_77) running on CompliantID's data.
 *
 * Layout and sections mirror the Jaybee report exactly: decision banner with
 * counter tiles, MLRO Conditions & Notes, Website Pre-Screening, Requirements
 * Coverage, Supporting Documents matrix, progressive-disclosure full detail
 * (What the MLRO has established, sections 1-9), Ask a Question, MLRO case
 * notes, and the decision panel. Differences are confined to: CompliantID's
 * table and column names, the second-signoff decision actions (Approve /
 * Return to compliance), firm-neutral wording, and a preview mode.
 *
 * Entry modes on /director-review:
 *   ?token=...              Signatory mode (director_reviews row by token).
 *   ?preview=1&session=...  Preview for a logged-in dashboard user — the
 *                           identical report, no decision panel.
 */

import { useState, useEffect, useRef } from "react";
import { supabase } from "./supabase";
import { buildVqfFormModel } from "./vqfFormLayout";
import { frameworkContext, INSTRUMENTS } from "./regulatoryFrameworks";
// The case flow map, reused verbatim from the dashboard (v1_544): the same
// computeFlowModel and the same renderer, so the signatory sees exactly the
// picture the officer worked from — one code path, no re-implementation that
// could drift. jumpTo is omitted here (no panels to jump to), which hides the
// Open affordances; document View buttons route through this page's own
// signed-URL opener. theme-legacy.css arrives with the import, so the map's
// dark palette renders as a deliberate dark figure on the light report.
import { CaseFlowMap, computeFlowModel } from "./MLRODashboard";
import { partyScreeningRows, PARTY_STATUS_LABEL } from "./shared/party-screening.js";
import { financialRangeRows } from "./shared/financial-ranges.js";
import { reconcileCaseState } from "./shared/case-state.js";
import { isIndividualCase } from "./shared/individual-case.js";
import { individualFormALine } from "./shared/individual-record.js";
import { caseOwnershipThreshold } from "./shared/ownership-threshold.js";
import { reviewPeriodShown } from "./shared/review-period.js";
import { plainText } from './shared/plain-text.js';
import { sowLinkForPerson, sowNewInterviewPending } from './shared/sow-links.js';
import { plainTree } from './shared/plain-tree.js';
// v1_913: the override of open items, shown before the signatory decides.
import { overrideItemLines, OVERRIDE_COPY } from './shared/signoff-gate.js';
import { cleanModelText } from './shared/case-note-text.js';
import { tierInForce, ASSESSMENT_PENDING } from './shared/tier-in-force.js';
import { verificationMethod, ONLINE_ID_DOCS_WORDS } from './shared/identity-method.js';

// Ownership strings arrive as "92.20%", "~94.79-94.86% effective interest (…)", "50/50".
// parseFloat on a leading "~" is NaN, which silently EXCLUDED the principal UBO from the
// source-of-wealth section while a cleanly formatted corporate holder stayed in
// (Belmoney: Bruno Pedras dropped, XE Investments SARL shown, 25/07/26). Parse the first
// number anywhere in the string instead.
const ownershipPct = (v) => parseFloat(String(v || '').match(/[\d.]+/)?.[0] || '0');
// A PERSONAL source-of-wealth profile applies to natural persons only; a corporate
// shareholder has no personal wealth and its own owners appear as individual UBOs.
const _entitySuffix = /\b(s\.?a\.?r\.?l|s\.?a\.?|s\.?p\.?a|ltd|limited|llc|inc|plc|gmbh|ag|bv|nv|oy|ab|as|aps|kft|sp\s?z\s?o\s?o|pte|sdn|bhd|trust|foundation|stiftung|holdings?|investments?|partners|capital|group|company|corp(oration)?)\b\.?$/i;
// v1_830: at or above the case's ownership threshold (25 when the firm sets none).
const isPersonalSowUbo = (u, threshold = 25) => {
  if (!u || !u.name) return false;
  if (u.corporateUBO === true || u.corporateUBO === 'true') return false;
  if (_entitySuffix.test(String(u.name).trim())) return false;
  return ownershipPct(u.ownership) >= threshold;
};


// Migrate walk finding (14/10/2026): the firm's internal records (internal: true)
// are never shown in the review report, except the folder 8 prior sign off
// (internal_kind prior_signoff), which the reviewer sees by the Edgewater
// decision. Folder 9 prior screening is always left out. Applied on every path
// the page loads documents by: the server route and the direct read.
const hiddenFromReviewer = (d) => !!d && (d.internal_kind === 'prior_screening' || (d.internal === true && d.internal_kind !== 'prior_signoff'));
const withoutInternal = (rows) => (Array.isArray(rows) ? rows.filter(d => !hiddenFromReviewer(d)) : []);

export default function DirectorReview() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // ── OTP-1 (v1_462): review links are gated unconditionally. The server
  // answers 401 otpRequired until this browser verifies a code emailed to the
  // configured second-signatory address; the pass is a 24-hour HttpOnly cookie.
  const [otpGate, setOtpGate] = useState(null); // { token, maskedEmail } | null
  const [otpCode, setOtpCode] = useState("");
  const [otpError, setOtpError] = useState(null);
  const [otpBusy, setOtpBusy] = useState(false);
  const [otpResendIn, setOtpResendIn] = useState(0);
  useEffect(() => {
    if (otpResendIn <= 0) return;
    const t = setTimeout(() => setOtpResendIn(n => n - 1), 1000);
    return () => clearTimeout(t);
  }, [otpResendIn]);
  const otpRequestCode = async (tok) => {
    setOtpError(null);
    try {
      const r = await fetch("/api/otp-request", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ scope: shareTokenRef.current === tok ? "share" : "review", token: tok }) });
      const d = await r.json().catch(() => ({}));
      if (r.ok && d.sent) { setOtpGate(g => ({ ...(g || { token: tok }), maskedEmail: d.maskedEmail || (g && g.maskedEmail) })); setOtpResendIn(30); }
      else if (!r.ok) setOtpError(d.error || "The code could not be sent. Please try again shortly.");
    } catch (_) { setOtpError("The code could not be sent. Please check your connection and try again."); }
  };
  const otpVerifyCode = async () => {
    if (!otpGate || otpBusy) return;
    setOtpBusy(true); setOtpError(null);
    try {
      const r = await fetch("/api/otp-verify", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ scope: shareTokenRef.current === otpGate.token ? "share" : "review", token: otpGate.token, code: otpCode.trim() }) });
      const d = await r.json().catch(() => ({}));
      if (r.ok && d.ok) {
        const tok = otpGate.token;
        setOtpGate(null); setOtpCode(""); setLoading(true);
        loadReview(tok); // the pass cookie now opens the case
      } else {
        setOtpError(d.error || "That code did not verify. Please try again.");
        if (d.requestNew) setOtpCode("");
      }
    } catch (_) { setOtpError("Verification failed. Please check your connection and try again."); }
    finally { setOtpBusy(false); }
  };
  const [review, setReview] = useState(null);
  const [session, setSession] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [decision, setDecision] = useState(null);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [verifications, setVerifications] = useState([]);
  // Form A/K declaration viewer — the same breakdown the dashboard shows, so an external
  // reviewer can read what the signer actually declared rather than trusting "Completed by OTP".
  const [formView, setFormView] = useState(null);
  const [transcript, setTranscript] = useState([]);
  const [showTranscript, setShowTranscript] = useState(false);
  const [translationView, setTranslationView] = useState(null);
  const [firmCtx, setFirmCtx] = useState(null);
  // Form A in the exports (22/10/2026): an individual's Form A status is read
  // only from form rows read without error; until then it says not available.
  const [formRowsRead, setFormRowsRead] = useState(false);
  const [walletScreenings, setWalletScreenings] = useState([]);
  const [sowSessions, setSowSessions] = useState([]);
  const [sowDocs, setSowDocs] = useState({});
  const [expandedSow, setExpandedSow] = useState({});
  const [showDetail, setShowDetail] = useState(false);
  const [caseNotes, setCaseNotes] = useState([]);
  const [showNotes, setShowNotes] = useState(false);
  const [deepsignRequests, setDeepsignRequests] = useState([]);
  const [sumsubFormRequests, setSumsubFormRequests] = useState([]);
  const [attestations, setAttestations] = useState([]);
  const [registryChecks, setRegistryChecks] = useState([]);
  const [docTrackingRows, setDocTrackingRows] = useState([]);
  const [isPreview, setIsPreview] = useState(false);
  // FULL-REPORT EXTERNAL SHARE (v1_582): the same report, read-only, for an
  // outside party the officer chose. OTP scope 'share', routes get mode:'share'.
  const [shareMode, setShareMode] = useState(null);
  const shareTokenRef = useRef(null);
  const [vqfProbe, setVqfProbe] = useState(false);

  // Probe whether this case's firm is a VQF firm, so the VQF documents section shows even
  // if the report predates the vqfFirm stamp (no reprocess required). Token-safe.
  useEffect(() => {
    if (!session?.id) return;
    (async () => {
      try {
        const payload = review?.token ? { token: review.token, format: 'probe' } : { sessionId: session.id, format: 'probe' };
        const r = await fetch('/api/generate-vqf-risk-profile', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
        const j = await r.json().catch(() => ({}));
        if (j && j.vqf) setVqfProbe(true);
      } catch (_) { /* non-fatal */ }
    })();
  }, [session, review]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    const previewSession = params.get("session");
    const preview = params.get("preview") === "1" && previewSession;
    if (preview) { setIsPreview(true); loadPreview(previewSession); return; }
    if (!token) { setError("Invalid review link."); setLoading(false); return; }
    if (params.get("mode") === "share") { shareTokenRef.current = token; setShareMode({ pending: true }); }
    loadReview(token);
  }, []);

  // Shared loader for everything hanging off a session. Takes the session row
  // itself so email-fallback matching never reads stale component state.
  const loadCaseData = async (sess) => {
    const sessionId = sess.id;
    const { data: docs } = await supabase.from("session_documents").select("*").eq("session_id", sessionId).order("uploaded_at");
    setDocuments(withoutInternal(docs));
    const { data: verifs } = await supabase.from("sumsub_verifications").select("*").eq("session_id", sessionId);
    setVerifications(verifs || []);
    const { data: wallets } = await supabase.from("wallet_screenings").select("*").eq("session_id", sessionId);
    setWalletScreenings(wallets || []);
    const { data: cNotes } = await supabase.from("case_notes").select("*").eq("session_id", sessionId).order("created_at", { ascending: true });
    setCaseNotes(cNotes || []);
    const { data: dsReqs, error: dsErr } = await supabase.from("deepsign_requests").select("*").eq("session_id", sessionId).order("created_at", { ascending: false });
    setDeepsignRequests(dsReqs || []);
    // Sumsub Form A/K confirmations — the current route; a confirmed Sumsub
    // record satisfies the Form A/K requirement just like a signed DeepSign one.
    const { data: ssForms, error: ssErr } = await supabase.from("sumsub_form_requests").select("*").eq("session_id", sessionId).order("created_at", { ascending: false });
    setSumsubFormRequests(ssForms || []);
    setFormRowsRead(!dsErr && !ssErr && Array.isArray(dsReqs) && Array.isArray(ssForms));
    // MLRO document-requirement decisions (Covered by other documents / Not
    // required) — these drive the Supporting Documents matrix so waived items
    // do not show as Outstanding to the reviewer.
    const { data: dt } = await supabase.from("document_tracking").select("*").eq("session_id", sessionId);
    setDocTrackingRows(dt || []);

    // SOW interview sessions for UBOs — prefer the server-stamped parent link,
    // fall back to contact-email match for older cases.
    const { data: sowData } = await supabase.from("onboarding_sessions")
      .select("*")
      .in("status", ["sow_complete", "sow_interview"])
      .order("created_at");
    const linkedSow = (sowData || []).filter(s =>
      s.parent_session_id === sessionId || // v1_897: the durable column (the interview can rewrite collected_data)
      s.collected_data?.parentSessionId === sessionId ||
      (sess?.contact_email && s.contact_email?.toLowerCase() === sess.contact_email?.toLowerCase())
    );
    setSowSessions(linkedSow);
    for (const sow of linkedSow) {
      const { data: sd } = await supabase.from("session_documents").select("*").eq("session_id", sow.id).order("uploaded_at");
      setSowDocs(prev => ({ ...prev, [sow.id]: withoutInternal(sd) }));
    }
  };

  // Server-backed loader. The report's data now comes from /api/review-data, which
  // validates the reviewer token (or the previewer's JWT) and returns only that one case.
  // This is what allows the anonymous read policies on the underlying tables to be dropped.
  // Returns false if the route is unavailable, so the pre-existing direct-query path can
  // still serve the page during the transition; once the policies are withdrawn that
  // fallback simply fails closed.
  const applyCaseData = (d) => {
    setDocuments(withoutInternal(d.documents));
    setVerifications(d.verifications || []);
    setWalletScreenings(d.walletScreenings || []);
    setCaseNotes(d.caseNotes || []);
    setDeepsignRequests(d.deepsignRequests || []);
    setSumsubFormRequests(d.sumsubFormRequests || []);
    setAttestations(d.attestations || []);
    setRegistryChecks(d.registryChecks || []);
    setDocTrackingRows(d.docTracking || []);
    setSowSessions(d.sowSessions || []);
    setSowDocs(d.sowDocs || {});
    setTranscript(d.transcript || []);
    setFirmCtx(d.firm || null);
    setFormRowsRead(d.formRowsRead === true);
  };

  const loadFromServer = async (payload, authToken) => {
    try {
      const res = await fetch("/api/review-data", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}) },
        body: JSON.stringify(payload)
      });
      const d = await res.json().catch(() => null);
      if (!res.ok || !d || !d.success) return { ok: false, error: (d && d.error) || null, status: res.status };
      return { ok: true, data: d };
    } catch (e) {
      return { ok: false, error: null, status: 0 };
    }
  };

  const loadReview = async (token) => {
    try {
      const isShare = shareTokenRef.current === token;
      const srv = await loadFromServer(isShare ? { token, mode: "share" } : { token });
      if (srv.ok) {
        const rv = srv.data.review;
        setReview(rv);
        if (isShare) setShareMode(srv.data.shareMode || { recipient_name: null });
        if (!isShare && rv && rv.status !== "pending") { setSubmitted(true); setLoading(false); return; }
        setSession(srv.data.session);
        applyCaseData(srv.data);
        setLoading(false);
        return;
      }
      // OTP-1: verification required before the case is served. Show the code
      // screen and send the first code automatically.
      if (srv.status === 401) {
        setOtpGate(g => g || { token, maskedEmail: null });
        otpRequestCode(token);
        setLoading(false);
        return;
      }
      // A definitive rejection from the server is the answer; only fall through when the
      // route itself was unreachable.
      if (srv.error && srv.status >= 400 && srv.status < 500) { setError(srv.error); setLoading(false); return; }
      // NO direct-read fallback in signatory mode. Supporting it required two RLS policies
      // that scoped nothing: SELECT on director_reviews with a qual of `true`, and anon SELECT
      // on every onboarding_session holding an invite token. The anon key ships in the frontend
      // bundle and is public by design, so between them anyone could enumerate every review
      // token and every compliance report across all tenants. Resilience is not worth a
      // cross-tenant breach: if the server route is unreachable the reviewer sees an error,
      // which is the correct outcome. (Found by RLS audit, 25/07/26.)
      setError("The review service is temporarily unavailable. Please try again shortly, or contact the compliance team if this continues.");
      setLoading(false);
      return;
    } catch(e) {
      setError("Unable to load review: " + e.message);
    }
    setLoading(false);
  };

  // Preview mode — a logged-in dashboard user opening the report for any case.
  // All reads run under their JWT, so firm-scoped RLS applies exactly as in
  // the dashboard itself.
  const loadPreview = async (sessionId) => {
    try {
      const { data: { session: authSession } } = await supabase.auth.getSession();
      if (!authSession) { setError("Preview requires a dashboard session you are signed in to. Please sign in to the dashboard and reopen this preview."); setLoading(false); return; }

      const srv = await loadFromServer({ sessionId }, authSession.access_token);
      if (srv.ok) {
        setSession(srv.data.session);
        applyCaseData(srv.data);
        setLoading(false);
        return;
      }
      if (srv.error && srv.status >= 400 && srv.status < 500) { setError(srv.error); setLoading(false); return; }
      console.warn("review-data unavailable, falling back to direct reads");

      const { data: sess, error: se } = await supabase.from("onboarding_sessions").select("*").eq("id", sessionId).single();
      if (se || !sess) { setError("Case not found or you do not have access to it."); setLoading(false); return; }
      setSession(sess);
      await loadCaseData(sess);
    } catch(e) {
      setError("Unable to load preview: " + e.message);
    }
    setLoading(false);
  };

  const submitDecision = async (dec) => {
    if (dec === "info" && !notes.trim()) { alert("Please add notes for the compliance team explaining what needs to be addressed."); return; }
    setSubmitting(true);
    try {
      const res = await fetch("/api/director-decision", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: review.token, decision: dec, notes, decidedBy: review?.signatory_name || "Second signatory" })
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error);
      setDecision(dec);
      setSubmitted(true);
    } catch(e) {
      alert("Error: " + e.message);
    }
    setSubmitting(false);
  };

  // Open a storage file. Opens the tab synchronously (so popup blockers allow
  // it), then resolves a URL via, in order: a browser-side signed URL, the
  // server route (token-authorised for the signatory; JWT-authorised in
  // preview), and the stored file_url from upload time. Surfaces the real
  // error rather than failing silently.
  const openFile = async (filePath, fileUrl) => {
    const win = window.open("", "_blank");
    const land = (url) => { if (win) { win.location.href = url; } else { window.open(url, "_blank"); } };
    let lastError = "";
    try {
      if (filePath) {
        try {
          const { data, error } = await supabase.storage.from("onboarding-documents").createSignedUrl(filePath, 3600);
          if (data?.signedUrl) { land(data.signedUrl); return; }
          if (error?.message) lastError = error.message;
        } catch (e) { lastError = e.message || String(e); }
        try {
          const headers = { "Content-Type": "application/json" };
          const payload = { path: filePath };
          if (shareTokenRef.current) { payload.token = shareTokenRef.current; payload.mode = "share"; }
          else if (review?.token) payload.token = review.token;
          else if (isPreview && session?.id) {
            payload.sessionId = session.id;
            const { data: { session: auth } } = await supabase.auth.getSession();
            if (auth?.access_token) headers["Authorization"] = "Bearer " + auth.access_token;
          }
          if (payload.token || payload.sessionId) {
            const r = await fetch("/api/review-file-url", { method: "POST", headers, body: JSON.stringify(payload) });
            const j = await r.json().catch(() => ({}));
            if (j.url) { land(j.url); return; }
            if (j.error) lastError = j.error;
          }
        } catch (e) { lastError = e.message || String(e); }
      }
      if (fileUrl) { land(fileUrl); return; }
      if (win) win.close();
      alert("Could not open this file" + (lastError ? ":\n\n" + lastError : ".") + "\n\nPlease contact the compliance team if the problem persists.");
    } catch (e) {
      if (win) win.close();
      alert("Could not open this file: " + (e.message || e));
    }
  };
  const getSignedUrl = (filePath) => openFile(filePath, null);

  // Open the Sumsub identity report for a verification row. Uses the stored copy
  // if present, otherwise fetches it on demand. Resolves historical rows via
  // externalUserId.
  const openSumsubReport = async (v) => {
    if (!v) return;
    try {
      let path = v.report_path;
      if (!path) {
        // Reached only when no report is stored for this person, so it has to be fetched
        // from Sumsub on demand. The call carried no credentials and the route requires
        // one, so it answered "Not signed in" for anyone whose report was not already
        // in storage.
        const headers = { "Content-Type": "application/json" };
        const urlToken = new URLSearchParams(window.location.search).get("token");
        const tok = review?.token || urlToken || null;
        if (!tok) {
          const { data: { session: auth } } = await supabase.auth.getSession();
          if (!auth?.access_token) {
            alert("The Sumsub report is not stored for this person yet, and fetching it needs either a dashboard session you are signed in to, or a review link.");
            return;
          }
          headers.Authorization = "Bearer " + auth.access_token;
        }
        const r = await fetch("/api/sumsub-report", { method: "POST", headers, body: JSON.stringify({ sessionId: v.session_id, applicantId: v.applicant_id, externalUserId: v.external_user_id, reviewToken: tok }) });
        const j = await r.json().catch(() => ({}));
        path = j.path;
        if (!path && j.error) { alert("Sumsub report could not be retrieved:\n\n" + j.error); return; }
      }
      if (!path) { alert("The Sumsub report is not available yet for this person."); return; }
      const { data, error } = await supabase.storage.from("onboarding-documents").createSignedUrl(path, 300);
      if (data?.signedUrl) { window.open(data.signedUrl, "_blank"); return; }
      {
        const headers2 = { "Content-Type": "application/json" };
        const payload2 = { path };
        if (shareTokenRef.current) { payload2.token = shareTokenRef.current; payload2.mode = "share"; }
        else if (review?.token) payload2.token = review.token;
        else if (isPreview && session?.id) {
          payload2.sessionId = session.id;
          const { data: { session: auth2 } } = await supabase.auth.getSession();
          if (auth2?.access_token) headers2["Authorization"] = "Bearer " + auth2.access_token;
        }
        if (payload2.token || payload2.sessionId) {
          const r2 = await fetch("/api/review-file-url", { method: "POST", headers: headers2, body: JSON.stringify(payload2) });
          const j2 = await r2.json().catch(() => ({}));
          if (j2.url) { window.open(j2.url, "_blank"); return; }
        }
      }
      alert("Could not open the Sumsub report." + (error ? "\n\n" + error.message : ""));
    } catch (e) { alert("Could not open the Sumsub report: " + (e.message || e)); }
  };

  // Open the AML screening report (adverse media / sanctions / PEP), rendered on
  // demand server-side and written into a new tab.
  const openAmlReport = async (v, personName, personRole) => {
    if (!v) return;
    const win = window.open("", "_blank");
    if (win) { try { win.document.write('<!doctype html><meta charset="utf-8"><title>AML screening report</title><body style="margin:0;font:16px -apple-system,BlinkMacSystemFont,Segoe UI,Roboto,sans-serif;color:#334155;padding:28px">Generating the AML screening report...</body>'); } catch (_) {} }
    try {
      // This sent no Authorization header, and api/sumsub-aml requires a firm member,
      // so it answered "Not signed in" every time it was called from this report.
      const headers = { "Content-Type": "application/json" };
      const urlToken = new URLSearchParams(window.location.search).get("token");
      const tok = review?.token || urlToken || null;
      if (!tok) {
        const { data: { session: auth } } = await supabase.auth.getSession();
        if (!auth?.access_token) {
          if (win) win.close();
          alert("The AML screening report needs either a dashboard session you are signed in to, or a review link.\n\nSign in to the dashboard and reopen this preview.");
          return;
        }
        headers.Authorization = "Bearer " + auth.access_token;
      }
      const r = await fetch("/api/sumsub-aml", { method: "POST", headers, body: JSON.stringify({ sessionId: v.session_id, applicantId: v.applicant_id, externalUserId: v.external_user_id, personName, personRole, reviewToken: tok }) });
      const j = await r.json().catch(() => ({}));
      if (j.html) {
        if (win) { try { win.document.open(); win.document.write(j.html); win.document.close(); } catch (_) {} }
        else { window.location.href = URL.createObjectURL(new Blob([j.html], { type: "text/html;charset=utf-8" })); }
      } else {
        if (win) win.close();
        alert("AML screening report could not be retrieved:\n\n" + (j.error || "Unknown error"));
      }
    } catch (e) { if (win) win.close(); alert("Could not open the AML screening report: " + (e.message || e)); }
  };

  // Wrap an async open action with pressed-state feedback on the button.
  const withPressFeedback = (fn) => async (ev) => {
    const btn = ev.currentTarget;
    const orig = btn.textContent;
    btn.textContent = "Opening...";
    btn.disabled = true;
    btn.style.opacity = "0.6";
    try { await fn(); } finally { btn.textContent = orig; btn.disabled = false; btn.style.opacity = "1"; }
  };

  const REPORT_STATUSES = ["approved", "approved_with_flags", "rejected", "requires_review"];

  const S = {
    page: { minHeight: "100vh", background: "#f8fafc", fontFamily: "Georgia, serif", color: "#111827" },
    header: { background: "#1e2d5a", padding: "20px 40px", display: "flex", justifyContent: "space-between", alignItems: "center" },
    container: { maxWidth: 900, margin: "0 auto", padding: "32px 24px" },
    section: { background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, padding: "24px 28px", marginBottom: 20 },
    sectionTitle: { fontSize: 11, fontWeight: 700, color: "#1e2d5a", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 16, paddingBottom: 8, borderBottom: "2px solid #1e2d5a" },
    kv: { display: "flex", gap: 8, marginBottom: 8 },
    kvLabel: { fontSize: 12, color: "#6b7280", minWidth: 140, flexShrink: 0, fontFamily: "Arial, sans-serif" },
    kvVal: { fontSize: 13, color: "#111827", fontFamily: "Arial, sans-serif", lineHeight: 1.5 },
    para: { fontSize: 13, color: "#374151", lineHeight: 1.8, fontFamily: "Arial, sans-serif", marginBottom: 8 },
    badge: (color) => ({ display: "inline-block", padding: "3px 10px", borderRadius: 12, fontSize: 11, fontWeight: 700, background: color + "20", border: "1px solid " + color + "40", color }),
    docRow: { display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", borderBottom: "1px solid #f3f4f6" },
    btn: (bg, color) => ({ padding: "12px 28px", background: bg, color, border: "none", borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "Arial, sans-serif" }),
  };

  // ── OTP-1 verification screen. The review exposes the officer's side of the
  // file, so nothing renders until the emailed code is confirmed. ──
  if (otpGate) return (
    <div style={{ ...S.page, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Arial, sans-serif" }}>
      <div style={{ background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: 14, padding: "36px 40px", width: 440, maxWidth: "94%", textAlign: "center", boxShadow: "0 4px 24px rgba(0,0,0,0.06)" }}>
        <div style={{ fontSize: 20, fontWeight: 700, color: "#111827", marginBottom: 8 }}>Confirm it's you</div>
        <div style={{ fontSize: 13.5, color: "#6b7280", lineHeight: 1.7, marginBottom: 22 }}>
          This compliance review is protected. We have emailed a 6-digit code to {otpGate.maskedEmail ? <span style={{ color: "#0d9488", fontWeight: 600 }}>{otpGate.maskedEmail}</span> : "the address this review was sent to"}. Enter it below to open the file.
        </div>
        <input
          value={otpCode}
          onChange={e => { setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6)); setOtpError(null); }}
          onKeyDown={e => { if (e.key === "Enter" && otpCode.length === 6) otpVerifyCode(); }}
          inputMode="numeric"
          autoComplete="one-time-code"
          autoFocus
          placeholder="••••••"
          style={{ width: "100%", boxSizing: "border-box", background: "#f9fafb", border: "1px solid #d1d5db", borderRadius: 10, color: "#111827", fontSize: 28, fontWeight: 600, letterSpacing: "12px", textAlign: "center", padding: "13px 0 13px 12px", outline: "none", marginBottom: 14 }}
        />
        {otpError && <div style={{ fontSize: 12.5, color: "#dc2626", lineHeight: 1.6, marginBottom: 12 }}>{otpError}</div>}
        <button
          onClick={otpVerifyCode}
          disabled={otpBusy || otpCode.length !== 6}
          style={{ width: "100%", background: otpBusy || otpCode.length !== 6 ? "#99d6d0" : "#0d9488", color: "#ffffff", border: "none", padding: "13px 0", borderRadius: 8, fontWeight: 700, fontSize: 14.5, cursor: otpBusy || otpCode.length !== 6 ? "default" : "pointer", marginBottom: 14 }}
        >{otpBusy ? "Verifying..." : "Open the review"}</button>
        <button
          onClick={() => otpRequestCode(otpGate.token)}
          disabled={otpResendIn > 0}
          style={{ background: "none", border: "none", color: otpResendIn > 0 ? "#9ca3af" : "#0d9488", fontSize: 13, cursor: otpResendIn > 0 ? "default" : "pointer", textDecoration: "underline" }}
        >{otpResendIn > 0 ? `Send a new code (${otpResendIn}s)` : "Send a new code"}</button>
        <div style={{ marginTop: 18, fontSize: 11.5, color: "#9ca3af", lineHeight: 1.6 }}>The code expires after 10 minutes. Once verified, this browser stays verified for 24 hours.</div>
      </div>
    </div>
  );

  if (loading) return (
    <div style={{ ...S.page, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ textAlign: "center", fontFamily: "Arial, sans-serif" }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>⏳</div>
        <div style={{ color: "#6b7280" }}>Loading review file...</div>
      </div>
    </div>
  );

  if (error) return (
    <div style={{ ...S.page, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ textAlign: "center", maxWidth: 420, fontFamily: "Arial, sans-serif" }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>⚠️</div>
        <div style={{ color: "#dc2626", marginBottom: 8, fontWeight: 600 }}>{error}</div>
        <div style={{ color: "#6b7280", fontSize: 13 }}>Please contact your compliance team if you believe this is in error.</div>
      </div>
    </div>
  );

  if (submitted) return (
    <div style={{ ...S.page, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ textAlign: "center", maxWidth: 500, fontFamily: "Arial, sans-serif" }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>{review?.status === "approved" || decision === "approve" ? "✅" : review?.status === "declined" || decision === "decline" ? "❌" : "📋"}</div>
        <div style={{ fontSize: 20, fontWeight: 700, color: "#1e2d5a", marginBottom: 8 }}>
          {decision === "approve" || review?.status === "approved" ? "File approved" : "Returned to compliance"}
        </div>
        <div style={{ color: "#6b7280", fontSize: 14, lineHeight: 1.7 }}>
          {decision === "approve" ? "Thank you. Your approval has been recorded and the compliance team has been notified." :
           decision === "info" ? "Thank you. The file has been returned to the compliance team with your notes. They will follow up accordingly." :
           "This review has already been completed."}
        </div>
        {review?.decision_notes && <div style={{ marginTop: 16, padding: "12px 16px", background: "#f9fafb", borderRadius: 8, fontSize: 13, color: "#374151", textAlign: "left" }}><strong>Your notes:</strong> {review.decision_notes}</div>}
      </div>
    </div>
  );

  // Safely coerce any report array item to a string.
  // The AI occasionally wraps string values in objects e.g. {note:"..."} — this guards all renders.
  // v1_813: toStr only feeds display, so model text is cleaned of dashes here.
  const toStr = v => {
    if (!v) return "";
    if (typeof v === "string") return plainText(v);
    if (typeof v === "object") return plainText(v.note || v.text || v.content || v.value || v.flag || v.finding || JSON.stringify(v));
    return String(v);
  };

  // CompliantID's session_documents columns are file_name / doc_type; migrated
  // rows may carry document_name / document_type. Read both.
  // The firm's own regulatory framework drives how this report frames itself and which
  // requirements it presents. A firm with nothing configured gets the full set, unchanged
  // from before this existed — under-showing a requirement is the dangerous direction.
  // Hotfix 13/10/2026: report is declared before the instrument flags below read
  // it. It was declared after them, so for a firm whose framework has no Form A/K
  // (every framework but the Swiss one) the flags reached report?.vqfFirm before
  // its declaration and the page crashed ("Cannot access ... before
  // initialization", production Sentry trace bd7512e4db9ac19f847338293eaaa1b7).
  const report = session?.report || {};
  const fw = frameworkContext(firmCtx?.complianceConfig);
  // A Swiss instrument must not appear for a firm the Swiss framework does not apply to.
  // vqfFirm / vqfProbe remain as a fallback for reports predating the framework config.
  const showSwissInstruments = fw.has(INSTRUMENTS.FORM_AK) || report?.vqfFirm || vqfProbe;
  const showVqfProfiles = fw.has(INSTRUMENTS.VQF_PROFILES) || report?.vqfFirm || vqfProbe;
  const showOnlineIdentification = fw.has(INSTRUMENTS.ONLINE_IDENTIFICATION) || report?.vqfFirm || vqfProbe;

  const docName = d => d?.file_name || d?.document_name || "";
  const docType = d => {
    let a = d?.analysis;
    if (typeof a === "string") { try { a = JSON.parse(a); } catch (_) { a = null; } }
    return d?.doc_type || d?.document_type || a?.docType || "";
  };

  // v1_747: ONE reconciled case state. The Review report reads outstanding items,
  // check states, SoW/SoF acceptance, screening, ownership and the verification
  // party set from here, never the raw report fields, so it can never contradict
  // the board, the sign off dialog or the rationale it is sent alongside.
  const cs = reconcileCaseState({
    report,
    collected_data: session?.collected_data,
    origin: session?.origin,
    documents,
    documentTracking: docTrackingRows,
    sumsubVerifications: Object.fromEntries((verifications || []).filter(v => v && v.person_name).map(v => [v.person_name, v])),
    aiReview: cleanModelText(session?.ai_review),
    walletScreenings,
    mlroDecision: session?.mlro_decision,
    decisionRecord: session?.collected_data?.decisionRecord,
    // v1_813: the same inputs the board passes, so the two cannot differ: the
    // establishing person (the one identity set), the officer's document
    // acknowledgements, and the firm's features (which checks apply).
    contactName: session?.contact_name,
    contactEmail: session?.contact_email,
    documentAcknowledgments: session?.document_acknowledgments,
    // Form A in the exports (22/10/2026): an individual's Form A rows and its
    // state from the case's regime, as the board passes them.
    ...(isIndividualCase(session?.collected_data) ? { formRequests: formRowsRead ? [...(sumsubFormRequests || []), ...(deepsignRequests || [])] : undefined, individualFormA: firmCtx ? firmCtx.individualFormA : undefined } : {}),
  }, firmCtx && firmCtx.features ? { features: firmCtx.features } : {});
  // v1_813: a coverage row with a board check reads the board's reconciled
  // state; a row the board has no check for is "Noted" when unmet, never an
  // Outstanding the board does not have. The report read "3 Outstanding" while
  // the board was at Decide with nothing outstanding (walk, runs 23 to 25/09).
  const boardChk = (key) => [...(cs.checks || []), ...(cs.procedural || [])].find(c => c && c.key === key) || null;
  const boardOk = (key, fallback) => { const c = boardChk(key); return c ? !!c.done : fallback; };
  const cd = report.companyDetails || {};
  const fc = report.finsaClassification || {};
  const finsaCategory = typeof fc === "object" ? (fc.category || "Not classified") : String(fc || "Not classified");
  const finsaBasis = typeof fc === "object" ? fc.basis : null;
  // Model tier pending (29/10/2026): the tier in force, never the model's riskTier.
  const tierNow = tierInForce(report);
  const riskColor = !tierNow ? "#d97706" : tierNow === "High" ? "#dc2626" : tierNow === "Medium" ? "#d97706" : "#16a34a";

  // ----- Decision-ready model (mirrors the Sage Jaybee report) ---------------
  const fmt = (...xs) => xs.map(toStr).filter(x => x && x !== "...").join(" · ");
  const ap = report.activityProfile || {};
  const vp = report.vqfProfile || {};
  const entityType = toStr(report.entityProfile?.entityType) || toStr(report.clientType) || "";
  const mlroRec = toStr(report.mlroRecommendation) || session?.mlro_decision || "Approve";
  const recNorm = (mlroRec || "").toLowerCase();
  const recApprove = recNorm.includes("approve") && !recNorm.includes("do not approve");
  const recDecline = recNorm.includes("decline") || recNorm.includes("do not approve");
  const recColor = recApprove ? "#16a34a" : recDecline ? "#dc2626" : "#d97706";
  const recBg = recApprove ? "#f0fdf4" : recDecline ? "#fef2f2" : "#fff7ed";
  const reviewRef = (review?.id || session?.id || "").toString().slice(0, 8).toUpperCase();
  const reviewedBy = review?.created_by || "Compliance team";

  // Corporate shareholders must not appear as needing a PERSONAL source-of-wealth
  // interview: XE Investments SARL (92.20% of Belmoney) was shown as "pending" on a report
  // sent for second sign-off, when its own owner Bruno Pedras is the natural-person UBO and
  // is assessed in his own right (25/07/26).
  const ownT = caseOwnershipThreshold(report);
  const ubos25List = (report.ubos || []).filter(u => isPersonalSowUbo(u, ownT));
  const sumsubApproved = verifications.filter(v => v.status === "approved").length;
  const hasCrypto = walletScreenings.length > 0;
  const treasuryName = fc.treasuryContactName;

  // Mirror the dashboard: key verifications by exact person_name, with a normalised fallback,
  // and drive identity status from report.sumsubRequired (directors + UBOs + signatories).
  const verifByName = {};
  verifications.forEach(v => { if (v?.person_name) verifByName[v.person_name] = v; });
  const _norm = (s) => String(s || "").toLowerCase().replace(/\s+/g, " ").trim();
  const verifFor = (name) => {
    if (!name) return null;
    if (verifByName[name]) return verifByName[name];
    const t = _norm(name); if (!t) return null;
    const exact = verifications.find(v => _norm(v.person_name) === t); if (exact) return exact;
    const parts = t.split(" ").filter(Boolean); const first = parts[0], last = parts[parts.length - 1];
    return verifications.find(v => { const n = _norm(v.person_name); return first && last && n.includes(first) && n.includes(last); })
      || verifications.find(v => { const n = _norm(v.person_name); return first && n.includes(first); }) || null;
  };
  // v1_747: the verification party set is the reconciled, deduped set (a person
  // who is both a director and a signatory counts once), never the raw
  // report.sumsubRequired which over-lists (Nathan appeared as both, reading "3 of
  // 3" where the case has two parties).
  const reqPeople = cs.verificationParties;
  const reqApproved = cs.verifiedCount;
  const entitySanctions = session?.collected_data?.entitySanctions || null;

  // A UBO's SOW is satisfied by an assessment on the report OR a completed
  // linked SOW interview session (existing cases may only have the latter).
  // v1_897: the same rule as the dashboard: the person's most recent completed
  // interview, then the newest open link, matched by the person matcher (a first
  // name match took a sibling's interview, and the first link found could be an
  // unused resend). sowSessions are already this case's links.
  const sowSessionFor = (ubo) => sowLinkForPerson(sowSessions, null, ubo).link;
  // Fetch what the signer declared on Form A/K, scoped by this review's own signatory token:
  // the route resolves the token to its session and refuses an applicant that does not belong
  // to it. Mirrors the dashboard's compliance View, using the same shared model builder so the
  // breakdown an external reviewer reads is the breakdown the MLRO read.
  const _prettyLabel = (id) => String(id || "")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\bLab\b/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^./, c => c.toUpperCase());
  const _prettyValue = (v) => {
    if (v === true) return "Yes";
    if (v === false) return "No";
    return String(v == null ? "" : v);
  };
  // Working English translation of a document, scoped by this review's signatory token.
  const openTranslation = async (doc) => {
    const token = new URLSearchParams(window.location.search).get("token");
    if (!token || !doc?.id) return;
    setTranslationView({ loading: true, doc });
    try {
      const r = await fetch("/api/translate-document", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ documentId: doc.id, reviewToken: token })
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || !d.success) throw new Error(d.error || `HTTP ${r.status}`);
      setTranslationView({ loading: false, doc, translation: d.translation, cached: d.cached });
    } catch (e) {
      setTranslationView({ loading: false, doc, error: e.message });
    }
  };

  const openFormView = async (row) => {
    // This required a review token in the URL and returned on its first line without it,
    // so in the dashboard preview the View button did nothing at all: no panel, no error,
    // no console entry. A silent return is the worst failure mode in the product, because
    // there is nothing for anyone to report. Preview now authenticates with the officer's
    // own session, and every path from here ends in something visible.
    const token = new URLSearchParams(window.location.search).get("token");
    if (!row?.applicantId) {
      setFormView({ loading: false, row, error: "No Sumsub applicant is recorded against this declaration, so there is nothing to retrieve." });
      return;
    }
    setFormView({ loading: true, row });
    try {
      const params = new URLSearchParams({ action: "form-answers", applicantId: row.applicantId });
      const headers = {};
      if (token) params.set("reviewToken", token);
      else if (session?.id) {
        params.set("sessionId", session.id);
        const { data: { session: auth } } = await supabase.auth.getSession();
        if (!auth?.access_token) throw new Error("Your dashboard session has expired. Sign in again and reopen this preview.");
        headers.Authorization = "Bearer " + auth.access_token;
      } else {
        throw new Error("This report was opened without a review link or a session you are signed in to.");
      }
      if (row.record?.applicant_action_id) params.set("applicantActionId", row.record.applicant_action_id);
      const res = await fetch(`/api/sumsub?${params.toString()}`, { headers });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || !d.success) throw new Error(d.error || `HTTP ${res.status}`);
      const model = buildVqfFormModel({
        record: row.record,
        questionnaire: d.questionnaire,
        session,
        report: report || {},
        fieldLabel: _prettyLabel,
        valueLabel: _prettyValue
      });
      setFormView({ loading: false, row, model, checks: d.checks || [] });
    } catch (e) {
      setFormView({ loading: false, row, error: e.message });
    }
  };

  // v1_747: an officer acceptance of source of wealth/funds (checkResolutions.funds,
  // surfaced by cs.sow.accepted) satisfies the UBO SoW coverage, so an accepted
  // case does not read "pending" after a reprocess rewrites the raw score.
  // v1_897: a new interview the officer asked for keeps the person pending until it completes.
  const sowSatisfied = (ubo) => cs.sow.accepted || (!sowNewInterviewPending(ubo) && !!(ubo.sowAssessment || ubo.sowInterviewComplete || (sowSessionFor(ubo)?.status === "sow_complete")));

  // Form A / K completion status — merges BOTH confirmation routes: DeepSign QES signatures and
  // Sumsub OTP confirmations. Requests are deduplicated per (form type, signer) so a
  // form sent via both routes counts once and is satisfied by whichever route completes first.
  // A declaration is satisfied by ONE signature from ONE authorised person on behalf of
  // the contracting party, either an authorised signatory or a director. It is not
  // satisfied per person. Counting by (form type, signer) made a case where the director
  // had already signed read "1 of 2 completed" because a second person had also been
  // sent the same form, and presented a complete file as outstanding to the second
  // signatory. The instrument is the unit, not the human. Both A and K can apply on the
  // same case, so each applicable instrument is counted separately.
  //
  // form_type / doc_type is read tolerantly: the two schemas disagree on the column name.
  const _ft = r => String((r && (r.form_type || r.doc_type)) || "").toUpperCase();
  const dsAK = (deepsignRequests || []).filter(r => (_ft(r) === "A" || _ft(r) === "K") && r.status !== "cancelled");
  const ssAK = (sumsubFormRequests || []).filter(r => (_ft(r) === "A" || _ft(r) === "K") && r.status !== "cancelled");
  const akMap = {};
  const akSignedBy = {};
  const akMark = (r, isDone, routeLabel) => {
    const k = _ft(r);
    if (!k) return;
    if (isDone) {
      akMap[k] = "done";
      if (!akSignedBy[k]) akSignedBy[k] = { name: r.signer_name || "authorised signatory", route: routeLabel };
    } else if (akMap[k] !== "done") akMap[k] = "pending";
  };
  dsAK.forEach(r => akMark(r, r.status === "signed", "DeepSign QES"));
  ssAK.forEach(r => akMark(r, r.status === "confirmed", "Sumsub OTP"));
  const akTotal = Object.keys(akMap).length;
  const akDone = Object.values(akMap).filter(v => v === "done").length;
  const formAKState = akTotal === 0 ? "none" : (akDone === akTotal ? "signed" : "sent");
  // Requests to anyone else for an instrument that is already signed are redundant, not
  // outstanding, and the report says so rather than leaving a signed file looking short.
  const akRedundant = [...dsAK, ...ssAK].filter(r =>
    akMap[_ft(r)] === "done" && r.status !== "signed" && r.status !== "confirmed" &&
    _norm(r.signer_name) !== _norm(akSignedBy[_ft(r)] && akSignedBy[_ft(r)].name)
  ).length;
  const akDoneLabels = Object.keys(akSignedBy).sort().map(k =>
    "Form " + k + " signed by " + akSignedBy[k].name + " (" + akSignedBy[k].route + ")");
  const formAKEvidence = formAKState === "none" ? "Not issued for this case"
    : formAKState === "signed"
      ? akDoneLabels.join("; ") + (akRedundant ? ". A further request to another signatory is redundant and needs no action." : "")
      : (akDone ? akDoneLabels.join("; ") + ". " : "") +
        "Awaiting signature on " + (akTotal - akDone) + " of " + akTotal + " applicable declaration" + (akTotal === 1 ? "" : "s");

  // MLRO requirement decisions from document_tracking — fuzzy-matched by normalised name so a
  // decision recorded against e.g. "Register of Directors" also covers close variants.
  const _trackNorm = s => (s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
  const trackFor = (label) => {
    const target = _trackNorm(label);
    if (!target) return null;
    let hit = docTrackingRows.find(t => _trackNorm(t.item_name) === target);
    if (hit) return hit;
    hit = docTrackingRows.find(t => {
      const k = _trackNorm(t.item_name);
      return k.length >= 6 && (target.includes(k) || k.includes(target));
    });
    return hit || null;
  };

  // Requirements coverage. Framework-aware: Swiss instruments (Form A/K, professional
  // treasury) are omitted entirely for a firm the Swiss framework does not apply to — a
  // Lithuanian EMI's signatory should never be asked to sign off against them — and each
  // universal row carries the citation from the firm's OWN framework.
  const coverage = [
    { label: "Company information",
      cite: fw.cite("identity"),
      board: 'legit',
      ok: boardOk('legit', !!(cd.legalName && cd.registrationNumber && cd.jurisdiction)),
      partial: !boardChk('legit') && !!(cd.legalName && !(cd.registrationNumber && cd.jurisdiction)),
      evidence: fmt(cd.legalName || session?.company_name, cd.jurisdiction, cd.registrationNumber ? "Reg " + cd.registrationNumber : "") || "Not confirmed" },
    { label: "Business activity & places of business",
      cite: fw.cite("businessActivity"),
      board: 'money',
      ok: boardOk('money', !!cd.businessDescription),
      evidence: (toStr(cd.businessDescription).slice(0, 120)) || "Not described" },
    { label: "Financial circumstances (turnover / assets)",
      board: boardChk('activity') ? 'activity' : null,
      ok: boardOk('activity', !!ap.annualTurnover),
      partial: !boardChk('activity') && !ap.annualTurnover && !!(ap.currencies || ap.monthlyTransactionVolume || financialRangeRows(session?.collected_data?.financialRanges).length),
      evidence: fmt(
        ap.annualTurnover ? "Turnover " + toStr(ap.annualTurnover) : "",
        vp.estimatedAssets ? "Assets " + toStr(vp.estimatedAssets) : "",
        vp.estimatedLiabilities ? "Liabilities " + toStr(vp.estimatedLiabilities) : "",
        ap.currencies ? toStr(ap.currencies) : "",
        report.sofAssessment?.namedAccount ? "SoF " + toStr(report.sofAssessment.namedAccount) : "",
        ...financialRangeRows(session?.collected_data?.financialRanges).map(r => r.label + " " + r.value)) || "Turnover / assets band not captured" },
    // This row previously passed whenever the company had ANY signatory or director, so a case
    // whose dashboard showed "AUTHORITY TO ACT — Not confirmed" in red was signed off with a
    // green tick and a list of signatory names (Belmoney/Gabriella Diamantino, 25/07/26). A
    // second signer would reasonably read that as the applicant's authority being established.
    // The requirement is about the APPLICANT's authority, so test that.
    { label: "Authority to act (applicant)",
      cite: fw.cite("authority"),
      ok: report.applicantAuthority?.authorityConfirmed === true,
      evidence: (() => {
        const aa = report.applicantAuthority || {};
        const who = toStr(aa.personName) || toStr(report.contactName) || "Applicant";
        const sigs = (report.authorisedSignatories || []).map(s => toStr(s.name || s)).filter(Boolean).join(", ");
        if (aa.authorityConfirmed === true) {
          return `${who}: authority confirmed${aa.authorityBasis ? " (" + toStr(aa.authorityBasis) + ")" : ""}${aa.evidenceAvailable ? "; documentary evidence on file" : "; applicant attestation only"}`;
        }
        const rests = aa.authorityHolderName ? ` Authority stated to rest with ${toStr(aa.authorityHolderName)}.` : "";
        const why = aa.declinedReason ? ` ${toStr(aa.declinedReason)}` : "";
        return `${who}: NOT confirmed.${why}${rests}${sigs ? ` Named signatories on file: ${sigs}.` : ""}`;
      })() },
    { label: "Entity type",
      ok: entityType !== "",
      evidence: entityType !== "" ? entityType : "Not classified" },
    // Form A in the exports (22/10/2026): an individual's Form A reads the
    // board's own check and says, in the Audit Record's words, confirmed, not
    // confirmed, not required, or not read (never "Not issued" from a failed read).
    ...(isIndividualCase(session?.collected_data) ? (firmCtx && firmCtx.individualFormA === "not_required" ? [] : [{ label: "Form A (signed by the client)",
      cite: fw.cite("formAK"),
      board: boardChk("formA"),
      ok: boardOk("formA", false),
      evidence: individualFormALine({ report, collected: session?.collected_data, formRequests: formRowsRead ? [...(sumsubFormRequests || []), ...(deepsignRequests || [])] : undefined, formAState: firmCtx ? firmCtx.individualFormA : undefined }) }])
    : showSwissInstruments ? [{ label: "Form A / K (signed)",
      cite: fw.cite("formAK"),
      ok: formAKState === "signed",
      partial: formAKState === "sent",
      na: akTotal === 0,
      evidence: formAKEvidence }] : []),
    ...(fw.has(INSTRUMENTS.PROFESSIONAL_TREASURY) || report?.vqfFirm || vqfProbe ? [{ label: "Professional treasury",
      cite: fw.cite("professionalTreasury"),
      ok: !!treasuryName,
      na: !treasuryName && !fc.treasuryContactEmail,
      evidence: treasuryName ? "Qualified person: " + fmt(treasuryName, fc.treasuryContactTitle) : "Not declared / not applicable" }] : []),
    // Three states, not two. "Asked and not confirmed" is a finding; "never asked" is a
    // gap in our process. Both used to print as "Not yet recorded", which understated the
    // first to whoever signs this off.
    { label: "Tax compliance declaration",
      cite: fw.cite("taxCompliance"),
      ok: report.taxCompliance?.declared === true && !report.taxCompliance?.riskIndicatorsPresent,
      partial: !!report.taxCompliance?.riskIndicatorsPresent || report.taxCompliance?.declared === false,
      evidence: report.taxCompliance?.riskIndicatorsPresent ? "Risk indicators present, see file"
        : report.taxCompliance?.declared === true ? "Declaration obtained"
        : report.taxCompliance?.declared === false ? "Client was asked and did not confirm"
        : "Not asked or no answer recorded" },
    { label: "Identity verification (Sumsub)",
      cite: fw.cite("identity"),
      // v1_747: denominated by the reconciled party set (reqPeople = cs.verificationParties), never the raw verification rows.
      board: 'sumsub',
      ok: reqPeople.length > 0 ? reqApproved === reqPeople.length : true,
      partial: reqPeople.length > 0 && reqApproved > 0 && reqApproved < reqPeople.length,
      evidence: reqPeople.length ? reqApproved + " of " + reqPeople.length + " individuals verified" : "No individuals listed" },
    { label: "Beneficial owners & controllers",
      cite: fw.cite("beneficialOwners"),
      board: 'ubo',
      ok: boardOk('ubo', (report.ubos || []).length > 0),
      evidence: (report.ubos || []).map(u => fmt(u.name, u.ownership ? toStr(u.ownership) : "")).filter(Boolean).join("; ") || "Not identified" },
    { label: `Source of wealth (UBOs at or above ${ownT}%)`,
      cite: fw.cite("sourceOfWealth"),
      board: boardChk('sow') ? 'sow' : null,
      ok: boardOk('sow', !!report.sowWaiver || ubos25List.length === 0 || ubos25List.every(sowSatisfied)),
      na: !!report.sowWaiver || ubos25List.length === 0,
      evidence: report.sowWaiver ? "Not required: " + toStr(report.sowWaiver.reason) + (report.sowWaiver.by ? " (MLRO waiver: " + report.sowWaiver.by + (report.sowWaiver.at ? ", " + new Date(report.sowWaiver.at).toLocaleDateString("en-GB") : "") + ")" : "")
        : ubos25List.length === 0 ? `No UBO at or above the ${ownT}% threshold`
        : ubos25List.map(u => fmt(u.name, sowSatisfied(u) ? "assessed" : "pending")).join("; ") },
    { label: "Wallet screening & Travel Rule",
      board: (boardChk('wallets') || boardChk('travelrule')) ? 'wallets' : null,
      ok: (boardChk('wallets') || boardChk('travelrule')) ? (boardOk('wallets', true) && boardOk('travelrule', true)) : (!hasCrypto || report.travelRule?.overallStatus === "compliant"),
      na: !hasCrypto,
      partial: !(boardChk('wallets') || boardChk('travelrule')) && hasCrypto && report.travelRule?.overallStatus !== "compliant",
      evidence: !hasCrypto ? "No crypto wallets declared"
        : walletScreenings.length + " wallet(s) screened · Travel Rule " + (report.travelRule?.overallStatus === "compliant" ? "compliant" : "pending") },
  ];
  // v1_813: a row with no board check is "noted" when unmet: shown, with its
  // evidence, but not an Outstanding the board does not carry.
  const covStatus = (c) => c.na ? "na" : c.ok ? "complete" : !c.board ? "noted" : c.partial ? "partial" : "outstanding";
  const coverageActive = coverage.filter(c => !c.na);
  const completeCount = coverageActive.filter(c => covStatus(c) === "complete").length;
  const outstandingCount = coverageActive.filter(c => ["outstanding", "partial"].includes(covStatus(c))).length;
  const notedCount = coverageActive.filter(c => covStatus(c) === "noted").length;

  // Supporting-documents matrix — driven by the case's own state rather than a
  // keyword checklist. Documents on file are listed under the AI's
  // classification of each file (it has read them, whatever they are named or
  // whatever language they are in); outstanding items come from the report's
  // own documentGaps, honouring the MLRO's tracking decisions exactly as the
  // dashboard does.
  const trackByName = {};
  docTrackingRows.forEach(t => { if (t?.item_name) trackByName[t.item_name] = t; });
  // v1_747: the document matrix reads the RECONCILED per-gap status (cs.documentGaps):
  // a gap whose request item is resolved or withdrawn is closed, so a case whose
  // gaps were all met through their request items no longer prints rows Outstanding.
  // A reconciled-closed gap ('resolved') renders as Covered.
  const gapRows = cs.documentGaps.map(g => {
    const track = trackByName[g.gap] || trackFor(g.gap);
    return { key: g.gap, track, status: g.status === 'resolved' ? 'received' : g.status };
  });

  // Render MLRO case notes readably: preserve line breaks, bold **labels**, lay out
  // - bullets and 1. numbered conditions. The MLRO enters these in a textarea with
  // markdown-style structure; a plain div collapses it into one block.
  const formatNotes = (text) => {
    if (!text) return null;
    const renderInline = (line) => line.split(/(\*\*[^*]+\*\*)/g).map((p, i) => {
      const m = p.match(/^\*\*([\s\S]+)\*\*$/);
      return m ? <strong key={i}>{m[1].trim()}</strong> : <span key={i}>{p}</span>;
    });
    return String(text).split(/\r?\n/).map((raw, i) => {
      const line = raw.trim();
      if (!line) return <div key={i} style={{ height: 6 }} />;
      const bullet = /^[-•]\s+/.test(line);
      const numbered = /^\d+\.\s+/.test(line);
      const content = bullet ? line.replace(/^[-•]\s+/, "") : line;
      return (
        <div key={i} style={{ display: "flex", gap: bullet ? 8 : 0, marginBottom: 5, paddingLeft: (bullet || numbered) ? 14 : 0, lineHeight: 1.6 }}>
          {bullet && <span style={{ color: "#9ca3af", flexShrink: 0 }}>•</span>}
          <span>{renderInline(content)}</span>
        </div>
      );
    });
  };

  // v1_822: every text node of the report is cleaned for display in one pass.
  return plainTree(
    <div style={S.page}>
      {/* Header */}
      <div style={S.header}>
        <div>
          <div style={{ fontSize: 11, color: "#84f1ed", letterSpacing: "1px", textTransform: "uppercase", marginBottom: 2 }}>CompliantID</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: "#fff" }}>Client Acceptance: Decision Review{isPreview ? " (Preview)" : shareMode ? " (Shared, read only)" : ""}</div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 11, color: "#84f1ed" }}>{isPreview ? "Internal preview, as the second signatory will see it" : shareMode ? ("Confidential, shared read only with " + (shareMode.recipient_name || shareMode.recipient_email || "an authorised external party") + ". No actions can be taken on this file.") : "Confidential, second signatory only"}</div>
          <div style={{ fontSize: 11, color: "#84f1ed" }}>{isPreview ? "Generated: " + new Date().toLocaleDateString("en-GB") : "Expires: " + ((shareMode?.expires_at || review?.expires_at) ? new Date(shareMode?.expires_at || review.expires_at).toLocaleDateString("en-GB") : "Not set")}</div>
        </div>
      </div>

      <div style={S.container}>
        {/* Decision banner */}
        <div style={{ background: recBg, border: `2px solid ${recColor}`, borderRadius: 8, padding: "18px 24px", marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
            <div>
              <div style={{ fontSize: 11, color: recColor, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", fontFamily: "Arial, sans-serif" }}>Internal MLRO Recommendation</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: recColor, fontFamily: "Arial, sans-serif", marginTop: 2 }}>{recApprove ? "APPROVE" : recDecline ? "DECLINE" : mlroRec}</div>
              <div style={{ fontSize: 13, color: "#374151", fontFamily: "Arial, sans-serif", marginTop: 6 }}>{fmt(cd.legalName || session?.company_name, cd.jurisdiction, entityType !== "" ? entityType : "") || "Not recorded"}</div>
            </div>
            <div style={{ textAlign: "right", fontFamily: "Arial, sans-serif" }}>
              <div style={{ marginBottom: 6 }}><span style={S.badge(riskColor)}>{tierNow ? tierNow + " risk" : ASSESSMENT_PENDING}</span></div>
              <div style={{ fontSize: 12, color: "#6b7280" }}>Reviewed by</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: "#111827" }}>{reviewedBy}</div>
              <div style={{ fontSize: 12, color: "#6b7280" }}>{session?.approved_at ? new Date(session.approved_at).toLocaleDateString("en-GB") : "Not approved"}{reviewRef ? " · Ref " + reviewRef : ""}</div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 12, marginTop: 16, flexWrap: "wrap" }}>
            {[
              ["Requirements complete", `${completeCount} / ${coverageActive.length}`, completeCount === coverageActive.length ? "#16a34a" : "#d97706"],
              ["Documents on file", `${documents.length}`, "#1e2d5a"],
              ["Items outstanding", `${outstandingCount}`, outstandingCount === 0 ? "#16a34a" : "#d97706"],
              ...(notedCount ? [["Noted, not on the case board", `${notedCount}`, "#6b7280"]] : []),
            ].map(([label, val, col]) => (
              <div key={label} style={{ flex: 1, minWidth: 140, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, padding: "12px 16px" }}>
                <div style={{ fontSize: 22, fontWeight: 700, color: col, fontFamily: "Arial, sans-serif" }}>{val}</div>
                <div style={{ fontSize: 11, color: "#6b7280", fontFamily: "Arial, sans-serif", textTransform: "uppercase", letterSpacing: "0.4px", marginTop: 2 }}>{label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* v1_913: AN APPROVAL WITH OPEN ITEMS, BY OVERRIDE. The MLRO approved
            with identity, document or condition items open, with a written
            reason and a named approver. Shown here, near the top and before the
            decision panel, so the signatory reads it before deciding. */}
        {(() => {
          const ov = session?.collected_data?.decisionRecord?.override;
          if (!ov || typeof ov !== "object") return null;
          const lines = overrideItemLines(ov);
          const when = ov.recordedAt ? new Date(ov.recordedAt).toLocaleString("en-GB") : "Not recorded";
          return (
            <div data-review-override style={{ ...S.section, borderLeft: "4px solid #d97706", background: "#fffbeb" }}>
              <div style={{ ...S.sectionTitle, color: "#92400e", borderBottomColor: "#d97706" }}>⚠ {OVERRIDE_COPY.reviewTitle}</div>
              <div style={{ ...S.para, marginBottom: 12 }}>{OVERRIDE_COPY.reviewIntro}</div>
              <div style={{ display: "grid", gridTemplateColumns: "max-content minmax(0, 1fr)", gap: "6px 14px", fontFamily: "Arial, sans-serif", fontSize: 13, marginBottom: 12 }}>
                <div style={{ color: "#6b7280" }}>{OVERRIDE_COPY.rowReason}</div>
                <div style={{ color: "#111827", whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{String(ov.reason || "")}</div>
                <div style={{ color: "#6b7280" }}>{OVERRIDE_COPY.rowApprover}</div>
                <div style={{ color: "#111827", fontWeight: 600, overflowWrap: "anywhere" }}>{String(ov.approver || "")}</div>
                <div style={{ color: "#6b7280" }}>{OVERRIDE_COPY.rowRecordedBy}</div>
                <div style={{ color: "#111827", overflowWrap: "anywhere" }}>{String(ov.recordedBy || "")}</div>
                <div style={{ color: "#6b7280" }}>{OVERRIDE_COPY.rowRecordedAt}</div>
                <div style={{ color: "#111827" }}>{when}</div>
              </div>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#92400e", textTransform: "uppercase", letterSpacing: "0.5px", fontFamily: "Arial, sans-serif", marginBottom: 6 }}>{OVERRIDE_COPY.rowItems} ({lines.length})</div>
              {lines.map((l, i) => (
                <div key={i} style={{ display: "flex", gap: 8, fontSize: 13, color: "#374151", fontFamily: "Arial, sans-serif", lineHeight: 1.6, marginBottom: 3 }}>
                  <span style={{ color: "#d97706", flexShrink: 0 }}>•</span><span style={{ overflowWrap: "anywhere" }}>{l}</span>
                </div>
              ))}
            </div>
          );
        })()}

        {/* MLRO conditions */}
        {session?.mlro_notes && (
          <div style={{ ...S.section, borderLeft: "4px solid #1e2d5a" }}>
            <div style={S.sectionTitle}>MLRO Conditions & Notes</div>
            <div style={{ ...S.para, marginBottom: 0 }}>{formatNotes(session.mlro_notes)}</div>
          </div>
        )}

        {/* ── The money flow, first (v1_544): the signatory reads how funds move
            through the structure before anything else, with every element
            carrying its evidence state — the same map, from the same code, the
            officer judged the case on. Non-interactive except document View
            (this page's own signed-URL opener) and the full-screen toggle. ── */}
        {(() => {
          if (!report || !session) return null;
          const cdd = session.collected_data || {};
          const esRaw = cdd.entitySanctions;
          let entityScreen = null;
          if (esRaw && typeof esRaw === 'object') {
            const dm = (esRaw.dismissed && typeof esRaw.dismissed === 'object') ? esRaw.dismissed : {};
            const allHits = [...(Array.isArray(esRaw.hits) ? esRaw.hits : []), ...(Array.isArray(esRaw.cautionMatches) ? esRaw.cautionMatches : [])];
            const live = allHits.filter(h => h && h.name && !dm[h.name]);
            const status = esRaw.keyRequired ? 'no_key' : esRaw.error ? 'error'
              : (esRaw.sanctioned && live.length) ? 'hit'
              : (esRaw.hasPartialMatches && live.length) ? 'caution'
              : allHits.length ? 'clear_with_notes' : 'clear';
            entityScreen = { status, data: esRaw };
          }
          const regEnvHere = (typeof window !== 'undefined' && /staging/.test(window.location.hostname)) ? 'sandbox' : 'production';
          let fm = null;
          try {
            fm = computeFlowModel(report, session, verifications, entityScreen, registryChecks, documents, walletScreenings, regEnvHere,
              // The SoW state the page already holds now reaches the map
              // (v1_557): passing empty arrays here made the report show
              // "awaiting sign-off" on an interview the dashboard showed
              // signed off, so the two maps disagreed about the same case
              // (Snowcap, 14/08/26). sowDocs is keyed by child id; the map
              // wants a flat list tagged with the child's name.
              sowSessions || [],
              Object.entries(sowDocs || {}).flatMap(([sid, arr]) => (arr || []).map(dd => ({ ...dd, _childName: ((sowSessions || []).find(sx => sx.id === sid) || {}).contact_name }))),
              attestations);
          } catch (e) { console.error('flow map failed on director report:', e && e.message); }
          if (!fm) return null;
          return (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#1e2d5a", fontFamily: "Georgia, serif", letterSpacing: "0.5px", textTransform: "uppercase", borderBottom: "2px solid #1e2d5a", paddingBottom: 6, marginBottom: 10 }}>Money Flow &amp; Evidence State</div>
              <CaseFlowMap model={fm} jumpTo={null} openDoc={async (d) => { if (d && (d.file_path || d.file_url)) await openFile(d.file_path, d.file_url); }} />
            </div>
          );
        })()}

        {/* ── How the tier was derived, compactly (v1_544): the signatory needs
            the tier, the score against the bands, the drivers, and any officer
            action on it — not the engine's internals. Rendered from the stored
            assessment, never recomputed here. ── */}
        {(() => {
          const ra = report.riskAssessment;
          if (!ra || !ra.tier) return null;
          const highDrivers = (ra.factors || []).filter(f => f && f.rating === 'High').map(f => f.factor).filter(Boolean).slice(0, 3);
          const adj = report.tierAdjustment;
          return (
            <div style={{ ...S.section, borderLeft: "4px solid #1e2d5a" }}>
              <div style={S.sectionTitle}>Risk Tier Derivation</div>
              <div style={{ fontSize: 12.5, color: "#374151", fontFamily: "Arial, sans-serif", lineHeight: 1.7 }}>
                The methodology ({ra.methodologyVersion || 'CID-RM'}) derived <b>{ra.tier}</b>
                {typeof ra.score === 'number' && ra.bands ? <> at score {ra.score.toFixed(2)} (Low ≤ {ra.bands.lowMax.toFixed(2)}, High ≥ {ra.bands.highMin.toFixed(2)})</> : null}
                {highDrivers.length ? <>. Principal drivers: {highDrivers.join('; ')}</> : null}
                {ra.eddRequired ? <>. Enhanced due diligence required</> : null}.
                {adj && adj.effectiveTier ? <> The case is operated at <b>{adj.effectiveTier}</b> by a recorded officer adjustment{adj.rationale ? <>: {toStr(adj.rationale)}</> : null}.</> : null}
                {report.tierMovement && report.tierMovement.to ? <> Tier movement on file: {report.tierMovement.from} to {report.tierMovement.to} ({toStr(report.tierMovement.trigger)}).</> : null}
              </div>
            </div>
          );
        })()}

        {/* External presence findings (v1_544). The "Website Pre-Screening"
            narrative section is gone as requested; what stays is only what a
            second signatory must not lose. Unresolved red flags from the web
            presence check (GTX: a domain overlap raising a same-operation
            question) are material findings — deleting them from the sign-off
            artefact would remove considered-and-recorded from the record. So:
            no flags, nothing renders (as before); flags with an MLRO
            mitigation note collapse to one green line; flags with NO note
            render as a compact list, without the narrative paragraph. */}
        {(() => {
          const wf = report.websiteFindings || {};
          const flags = (wf.redFlags || []).filter(f => f && f !== "...");
          const contras = (wf.contradictions || []).filter(c => c && c !== "...");
          if (!flags.length && !contras.length) return null;
          if (wf.mlroNote) return (
            <div style={{ ...S.section, borderLeft: "4px solid #16a34a" }}>
              <div style={S.sectionTitle}>External Presence Findings: Mitigated</div>
              <div style={{ fontSize:12, color:"#14532d", fontFamily:"Arial, sans-serif", lineHeight:1.6 }}>
                {flags.length + contras.length} finding{flags.length + contras.length === 1 ? "" : "s"} from the web presence check reviewed and mitigated by the MLRO: {toStr(wf.mlroNote)}
              </div>
            </div>
          );
          return (
            <div style={{ ...S.section, borderLeft: "4px solid #dc2626" }}>
              <div style={S.sectionTitle}>Unresolved External Findings</div>
              {flags.map((f, i) => (
                <div key={"f" + i} style={{ fontSize:12, color:"#dc2626", fontFamily:"Arial, sans-serif", lineHeight:1.6, marginBottom:4 }}>🚩 {toStr(f)}</div>
              ))}
              {contras.map((c, i) => (
                <div key={"c" + i} style={{ fontSize:12, color:"#d97706", fontFamily:"Arial, sans-serif", lineHeight:1.6, marginBottom:4 }}>⚠ {toStr(c)}</div>
              ))}
              <div style={{ marginTop:10, padding:"8px 12px", background:"#fff7ed", border:"1px solid #fed7aa", borderRadius:6, fontSize:12, color:"#92400e", fontFamily:"Arial, sans-serif" }}>
                ⚠ No MLRO mitigation note recorded. Recording one on the case collapses this section to a single mitigated line.
              </div>
            </div>
          );
        })()}
        {/* Requirements coverage — centrepiece */}
        {/* Object & Purpose of the Business Relationship — Art. 6 AMLA */}
        {(() => {
          const oap = report.objectAndPurpose || {};
          const has = !!(oap.object || oap.purpose);
          return (
            <div style={S.section}>
              <div style={S.sectionTitle}>Object &amp; Purpose of the Business Relationship</div>
              <div style={{ fontSize: 11, color: "#9ca3af", fontFamily: "Arial, sans-serif", marginBottom: 10 }}>Art. 6 AMLA: nature and purpose of the requested business relationship</div>
              {has ? (
                <>
                  {oap.object && <div style={{ marginBottom: 8 }}><div style={{ fontSize: 11, color: "#1e2d5a", fontWeight: 700, fontFamily: "Arial, sans-serif", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 3 }}>Object: requested service</div><div style={{ fontSize: 13, color: "#111827", fontFamily: "Arial, sans-serif", lineHeight: 1.6 }}>{String(oap.object)}</div></div>}
                  {oap.purpose && <div><div style={{ fontSize: 11, color: "#1e2d5a", fontWeight: 700, fontFamily: "Arial, sans-serif", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 3 }}>Purpose: why the client seeks the service</div><div style={{ fontSize: 13, color: "#111827", fontFamily: "Arial, sans-serif", lineHeight: 1.6 }}>{String(oap.purpose)}</div></div>}
                  {(oap.by || oap.at) && <div style={{ fontSize: 10, color: "#9ca3af", fontFamily: "Arial, sans-serif", marginTop: 8 }}>Documented by {String(oap.by || "MLRO")}{oap.at ? " · " + new Date(oap.at).toLocaleDateString("en-GB") : ""}</div>}
                </>
              ) : (
                <div style={{ fontSize: 12, color: "#92400e", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 6, padding: "8px 12px", fontFamily: "Arial, sans-serif" }}>
                  Not yet documented in structured form for this file. The underlying information is captured in the business profile and anticipated activity sections.
                </div>
              )}
            </div>
          );
        })()}

        {/* Entity Sanctions Screening — OpenSanctions (company + additional entities) */}
        {(() => {
          const normaliseScreening = (raw) => {
            if (!raw) return null;
            if (raw.status && !raw.entityChecked) return raw;
            if (raw.keyRequired) return { status: "no_key" };
            if (raw.error) return { status: "error" };
            const dismissed = raw.dismissed || {};
            const allMatches = [...(raw.hits || []), ...(raw.cautionMatches || [])];
            const allDismissed = allMatches.length > 0 && allMatches.every(h => dismissed[h.name]);
            if (allDismissed) return { status: "clear_with_notes", data: raw };
            if (raw.sanctioned) return { status: "hit", data: raw };
            if (raw.hasPartialMatches) return { status: "caution", data: raw };
            return { status: "clear", data: raw };
          };
          const es = normaliseScreening(session?.collected_data?.entitySanctions);
          const extras = (session?.collected_data?.additionalSanctionsScreenings || []).map(a => {
            const n = (a.data && (a.data.entityChecked || a.data.hits || a.data.sanctioned !== undefined)) ? normaliseScreening(a.data) : null;
            return { name: a.name, checkedAt: a.checkedAt, status: n?.status || a.status, data: n?.data || a.data || {} };
          });
          const st = es?.status;
          const d = es?.data || {};
          const head = st === "clear" ? ["#15803d", "✓ Clear"]
            : st === "clear_with_notes" ? ["#15803d", "✓ Clear (false positives noted)"]
            : st === "hit" ? ["#dc2626", "⚠ Sanctions hit"]
            : st === "caution" ? ["#d97706", "◈ Partial match, under review"]
            : ["#9ca3af", st === "running" ? "Screening…" : "Not yet run"];
          const badge = (c) => ({ display: "inline-block", padding: "2px 10px", borderRadius: 999, fontSize: 11, fontWeight: 700, color: "#fff", background: c, fontFamily: "Arial, sans-serif" });
          const dism = d.dismissed || {};
          const matches = [...(d.hits || []).map(h => ({ ...h, level: "hit" })), ...(d.cautionMatches || []).map(h => ({ ...h, level: "caution" }))];
          return (
            <div style={S.section}>
              <div style={S.sectionTitle}>Entity Sanctions Screening (OpenSanctions)</div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <div style={{ fontSize: 13, color: "#111827", fontFamily: "Arial, sans-serif", fontWeight: 600 }}>{String(d.entityChecked || session?.company_name || "Company")}</div>
                <span style={badge(head[0])}>{head[1]}</span>
              </div>
              <div style={{ fontSize: 11, color: "#6b7280", fontFamily: "Arial, sans-serif", marginBottom: 10 }}>
                {d.listsSearched ? String(d.listsSearched) : "OFAC, EU, UN, UK HMT, Swiss SECO + 200 others via OpenSanctions"}{d.checkedAt ? " · Screened " + new Date(d.checkedAt).toLocaleString("en-GB") : ""}
              </div>
              {matches.map((h, i) => {
                const dis = dism[h.name];
                return (
                  <div key={i} style={{ background: dis ? "#f0fdf4" : h.level === "hit" ? "#fff1f2" : "#fff7ed", border: "1px solid " + (dis ? "#bbf7d0" : h.level === "hit" ? "#fecaca" : "#fed7aa"), borderRadius: 6, padding: "8px 12px", marginBottom: 6 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: dis ? "#6b7280" : h.level === "hit" ? "#991b1b" : "#92400e", textDecoration: dis ? "line-through" : "none", fontFamily: "Arial, sans-serif" }}>{String(h.name)}: match {h.score}%</div>
                    <div style={{ fontSize: 11, color: "#6b7280", fontFamily: "Arial, sans-serif" }}>Lists: {(h.datasets || []).join(", ")}</div>
                    {dis && <div style={{ fontSize: 11, color: "#15803d", fontFamily: "Arial, sans-serif", marginTop: 4, paddingLeft: 8, borderLeft: "2px solid #bbf7d0" }}>✓ False positive: {String(dis.dismissedBy || "")}{dis.dismissedAt ? ", " + new Date(dis.dismissedAt).toLocaleDateString("en-GB") : ""}: {String(dis.reason || "")}</div>}
                  </div>
                );
              })}
              {extras.length > 0 && (
                <div style={{ marginTop: 10 }}>
                  <div style={{ fontSize: 10, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.5px", fontFamily: "Arial, sans-serif", fontWeight: 700, marginBottom: 6 }}>Additional Entities Screened (counterparties / related parties)</div>
                  {extras.map((a, ai) => {
                    const aPill = a.status === "hit" ? ["#dc2626", "⚠ Sanctions hit"] : a.status === "caution" ? ["#d97706", "◈ Partial match"]
                      : a.status === "clear_with_notes" ? ["#15803d", "✓ Clear (notes)"] : a.status === "clear" ? ["#15803d", "✓ Clear"] : ["#9ca3af", a.status || "Not yet run"];
                    const aDism = a.data?.dismissed || {};
                    return (
                      <div key={ai} style={{ marginBottom: 5 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, padding: "6px 10px", background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 6 }}>
                          <div style={{ minWidth: 0 }}>
                            <span style={{ fontSize: 12, fontWeight: 600, color: "#111827", fontFamily: "Arial, sans-serif" }}>{String(a.name)}</span>
                            <span style={{ fontSize: 10, color: "#9ca3af", fontFamily: "Arial, sans-serif", marginLeft: 8 }}>{a.checkedAt ? new Date(a.checkedAt).toLocaleDateString("en-GB") : ""}</span>
                          </div>
                          <span style={badge(aPill[0])}>{aPill[1]}</span>
                        </div>
                        {Object.entries(aDism).map(([hitName, dd], di) => (
                          <div key={di} style={{ fontSize: 11, color: "#6b7280", fontFamily: "Arial, sans-serif", padding: "4px 10px", marginTop: 3, background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 6 }}>
                            <span style={{ textDecoration: "line-through" }}>{String(hitName)}</span>
                            <span style={{ color: "#15803d" }}>, false positive confirmed by {String(dd.dismissedBy || "")}{dd.dismissedAt ? ", " + new Date(dd.dismissedAt).toLocaleDateString("en-GB") : ""}: {String(dd.reason || "")}</span>
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })()}

        <div style={S.section}>
          <div style={S.sectionTitle}>Requirements Coverage</div>
          <div style={{ ...S.para, marginBottom: 12 }}>
            Each acceptance requirement, with its status and the supporting evidence on file. Amber or red items are genuine gaps for your attention; they are the case board's own open conditions. A grey Noted row is a requirement the case board does not hold as a decision condition, shown with its evidence for your judgement. Requirements shown are those applicable under {fw.frameworkLine}{fw.configured ? "" : " (no framework configured for this firm, so the full requirement set is shown)"}.
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden" }}>
            <thead>
              <tr style={{ background: "#1e2d5a" }}>
                <th style={{ padding: "8px 12px", textAlign: "left", fontSize: 11, color: "white", fontFamily: "Arial, sans-serif", fontWeight: 700, width: 240 }}>Requirement</th>
                <th style={{ padding: "8px 12px", textAlign: "center", fontSize: 11, color: "white", fontFamily: "Arial, sans-serif", fontWeight: 700, width: 110 }}>Status</th>
                <th style={{ padding: "8px 12px", textAlign: "left", fontSize: 11, color: "white", fontFamily: "Arial, sans-serif", fontWeight: 700 }}>Evidence on file</th>
              </tr>
            </thead>
            <tbody>
              {coverage.map((c, i) => {
                const st = covStatus(c);
                const pill = st === "complete" ? ["#15803d", "✓ Complete"] : st === "partial" ? ["#d97706", "◐ Partial"] : st === "na" ? ["#9ca3af", "N / A"] : st === "noted" ? ["#6b7280", "◌ Noted"] : ["#dc2626", "✗ Outstanding"];
                return (
                  <tr key={i} style={{ background: i % 2 === 0 ? "#fff" : "#f9fafb", borderBottom: "1px solid #f3f4f6" }}>
                    <td style={{ padding: "9px 12px", fontSize: 13, color: "#111827", fontFamily: "Arial, sans-serif", fontWeight: 600 }}>
                      {c.label}
                      {/* The basis for the requirement, in the firm's own framework, so an
                          external reviewer can see WHY each row is here. */}
                      {c.cite ? <div style={{ fontSize: 10, fontWeight: 400, color: "#9ca3af", marginTop: 2 }}>{c.cite}</div> : null}
                    </td>
                    <td style={{ padding: "9px 12px", textAlign: "center" }}><span style={S.badge(pill[0])}>{pill[1]}</span></td>
                    <td style={{ padding: "9px 12px", fontSize: 12, color: (st === "complete" || st === "na") ? "#374151" : "#b45309", fontFamily: "Arial, sans-serif" }}>{plainText(c.evidence)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Supporting-documents matrix */}
        <div style={S.section}>
          <div style={S.sectionTitle}>Supporting Documents</div>
          <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden" }}>
            <thead>
              <tr style={{ background: "#1e2d5a" }}>
                <th style={{ padding: "8px 12px", textAlign: "left", fontSize: 11, color: "white", fontFamily: "Arial, sans-serif", fontWeight: 700 }}>Document</th>
                <th style={{ padding: "8px 12px", textAlign: "center", fontSize: 11, color: "white", fontFamily: "Arial, sans-serif", fontWeight: 700, width: 120 }}>Status</th>
                <th style={{ padding: "8px 12px", textAlign: "left", fontSize: 11, color: "white", fontFamily: "Arial, sans-serif", fontWeight: 700, width: 160 }}>File / Date</th>
              </tr>
            </thead>
            <tbody>
              {documents.map((d, i) => (
                <tr key={"doc" + i} style={{ background: i % 2 === 0 ? "#fff" : "#f9fafb", borderBottom: "1px solid #f3f4f6" }}>
                  <td style={{ padding: "9px 12px", fontSize: 13, color: "#111827", fontFamily: "Arial, sans-serif", fontWeight: 600 }}>
                    {docType(d) || docName(d)}
                    {docType(d) && docName(d) && docType(d) !== docName(d) && <div style={{ fontSize: 11, color: "#9ca3af", fontWeight: 400, marginTop: 2 }}>{docName(d)}</div>}
                  </td>
                  <td style={{ padding: "9px 12px", textAlign: "center" }}>{(d.file_path === 'pending' && !d.file_url) ? <span style={S.badge("#d97706")}>⚠ File not retained, upload it again</span> : <span style={S.badge(d.assessment === "Unsatisfactory" ? "#dc2626" : d.assessment === "Requires Clarification" ? "#d97706" : "#15803d")}>{d.assessment === "Unsatisfactory" ? "✗ Unsatisfactory" : d.assessment === "Requires Clarification" ? "◐ Clarification" : "✓ Received"}</span>}</td>
                  <td style={{ padding: "9px 12px", fontSize: 11, color: "#6b7280", fontFamily: "Arial, sans-serif" }}>
                    {((d.file_path && d.file_path !== 'pending') || d.file_url) ? <button onClick={() => openFile(d.file_path, d.file_url)} style={{ fontSize: 11, color: "#1e2d5a", background: "transparent", border: "1px solid #1e2d5a", borderRadius: 4, padding: "2px 8px", cursor: "pointer", fontFamily: "Arial, sans-serif", marginRight: 6 }}>View</button> : null}
                    {(d.uploaded_at || d.created_at) ? new Date(d.uploaded_at || d.created_at).toLocaleDateString("en-GB") : ""}
                  </td>
                </tr>
              ))}
              {/* Identity — always satisfied electronically, never an upload */}
              <tr style={{ background: documents.length % 2 === 0 ? "#fff" : "#f9fafb", borderBottom: "1px solid #f3f4f6" }}>
                <td style={{ padding: "9px 12px", fontSize: 13, color: "#111827", fontFamily: "Arial, sans-serif", fontWeight: 600 }}>UBO / controller ID &amp; proof of address</td>
                <td style={{ padding: "9px 12px", textAlign: "center" }}>
                  {reqPeople.length > 0 && reqApproved === reqPeople.length ? <span style={S.badge("#15803d")}>✓ Verified (Sumsub)</span>
                    : reqApproved > 0 ? <span style={S.badge("#d97706")}>◐ Sumsub partial</span>
                    : <span style={S.badge("#d97706")}>Outstanding</span>}
                </td>
                <td style={{ padding: "9px 12px", fontSize: 11, fontFamily: "Arial, sans-serif", color: reqPeople.length > 0 && reqApproved === reqPeople.length ? "#15803d" : "#b45309" }}>
                  {reqPeople.length > 0 ? `Electronic, Sumsub (${reqApproved}/${reqPeople.length})` : "No individuals listed"}
                </td>
              </tr>
              {/* Form A / K when issued for this case */}
              {akTotal > 0 && (
                <tr style={{ background: (documents.length + 1) % 2 === 0 ? "#fff" : "#f9fafb", borderBottom: "1px solid #f3f4f6" }}>
                  <td style={{ padding: "9px 12px", fontSize: 13, color: "#111827", fontFamily: "Arial, sans-serif", fontWeight: 600 }}>Form A / Form K</td>
                  <td style={{ padding: "9px 12px", textAlign: "center" }}><span style={S.badge(formAKState === "signed" ? "#15803d" : "#d97706")}>{formAKState === "signed" ? "✓ Confirmed" : "◐ Sent, awaiting"}</span></td>
                  <td style={{ padding: "9px 12px", fontSize: 11, fontFamily: "Arial, sans-serif", color: formAKState === "signed" ? "#15803d" : "#b45309" }}>
                    {formAKEvidence}
                    {/* The declared content of each Form A/K sits in its own section below, with a
                        View per signer. Point the reader at it from the row where they meet it. */}
                    <span style={{ color: "#6b7280" }}>, see Form A / Form K Declarations below for the declared content</span>
                  </td>
                </tr>
              )}
              {/* Outstanding / resolved items from the case's own document gaps */}
              {gapRows.map((g, i) => {
                const covered = g.status === "received";
                const notRequired = g.status === "waived" || g.status === "not_required";
                const outstanding = !covered && !notRequired;
                const trackBy = g.track ? (g.track.updated_by || "").split("@")[0] : "";
                const trackDate = g.track && g.track.updated_at ? new Date(g.track.updated_at).toLocaleDateString("en-GB") : "";
                return (
                  <tr key={"gap" + i} style={{ background: (documents.length + i) % 2 === 0 ? "#f9fafb" : "#fff", borderBottom: "1px solid #f3f4f6" }}>
                    <td style={{ padding: "9px 12px", fontSize: 13, color: notRequired ? "#9ca3af" : "#111827", fontFamily: "Arial, sans-serif", fontWeight: 600 }}>{plainText(g.key)}</td>
                    <td style={{ padding: "9px 12px", textAlign: "center" }}>
                      <span style={S.badge(covered ? "#15803d" : notRequired ? "#9ca3af" : "#d97706")}>{covered ? "✓ Covered" : notRequired ? "N / A, not required" : "Outstanding"}</span>
                    </td>
                    <td style={{ padding: "9px 12px", fontSize: 11, fontFamily: "Arial, sans-serif", color: covered ? "#15803d" : notRequired ? "#9ca3af" : "#b45309" }}>
                      {covered ? <span>{g.track?.waiver_reason ? toStr(g.track.waiver_reason) : "Confirmed on file by MLRO"}{trackBy ? ", " + trackBy : ""}{trackDate ? ", " + trackDate : ""}</span>
                        : notRequired ? <span>{g.track?.waiver_reason ? toStr(g.track.waiver_reason) : "Marked not required by MLRO"}{trackBy ? ", " + trackBy : ""}{trackDate ? ", " + trackDate : ""}</span>
                        : "None"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div style={{ fontSize: 11, color: "#9ca3af", fontFamily: "Arial, sans-serif", marginTop: 8 }}>
            Individual identity documents are verified electronically via Sumsub; the verification status of each person is in the full file detail below.
          </div>
        </div>

        {/* Progressive disclosure toggle */}
        <button onClick={() => setShowDetail(s => !s)} style={{ width: "100%", padding: "12px 16px", background: showDetail ? "#1e2d5a" : "#fff", color: showDetail ? "#fff" : "#1e2d5a", border: "1px solid #1e2d5a", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "Arial, sans-serif", marginBottom: 20 }}>
          {showDetail ? "▲ Hide full file detail" : "▼ Show full file detail: profile, ownership, source of funds & wealth, documents, sign off"}
        </button>

        {showDetail && (<>
        {/* Six Questions + Procedural Requirements */}
        {session && (() => {
          const r = report;
          const sumsubOk = verifications.some(v => v.status === "approved");

          const sixQuestions = [
            {
              q: "Is the company legitimate?",
              detail: r.companyDetails?.legalName ? `${r.companyDetails.legalName} · ${r.companyDetails.jurisdiction || ''} · Reg: ${r.companyDetails.registrationNumber || 'see file'}` : "Not confirmed",
              ok: !!(r.companyDetails?.legalName && r.companyDetails?.registrationNumber)
            },
            {
              q: "Are they regulated?",
              detail: r.regulatoryStatus?.regulated ? `${r.regulatoryStatus.regulator}, ${r.regulatoryStatus.licenceNumber || 'licence on file'}` : r.regulatoryStatus?.licenceType ? `Unregulated: ${r.regulatoryStatus.licenceType}. ${r.regulatoryStatus.assessment || ''}` : "Unregulated — see MLRO rationale",
              ok: !!(r.regulatoryStatus?.regulated || r.regulatoryStatus?.assessment)
            },
            {
              q: "Who is the UBO?",
              detail: (r.ubos || []).map(u => `${u.name} ${u.ownership}% (${u.nationality}, ${u.countryOfResidence || ''})`).join('; ') || "Not identified",
              ok: (r.ubos || []).length > 0
            },
            {
              q: "Who runs it?",
              // Roles were discarded here, so a director the platform had annotated
              // "(publicly reported - unconfirmed by client)" from web intelligence read as
              // an established officer alongside the register's own names — the reader could
              // not tell which were confirmed (Belmoney, six directors listed, 25/07/26).
              // Carry the annotation through; a plain "Director" adds nothing and is omitted.
              detail: (r.directors || []).map(d => {
                if (typeof d !== "object" || !d) return String(d || '');
                const role = String(d.role || '').trim();
                const worthShowing = role && !/^directors?$/i.test(role);
                return worthShowing ? `${d.name} (${role})` : d.name;
              }).filter(Boolean).join(', ') + ((r.authorisedSignatories || []).length > 0 ? ` · Signatories: ${r.authorisedSignatories.map(s => (typeof s === "object" ? s.name : s)).join(', ')}` : ''),
              ok: (r.directors || []).length > 0
            },
            {
              q: "How does the company make its money?",
              // The business model and the source of funds are DIFFERENT questions. This
              // one was gated on sofAssessment, so a file documenting the business in full
              // still reported "Not assessed" whenever the funding account was outstanding
              // — Belmoney, an NBB-authorised payment institution with its whole revenue
              // model on file, read as unassessed (25/07/26). Answer it from the business
              // description; funding is the next question down.
              detail: r.companyDetails?.businessDescription
                ? String(r.companyDetails.businessDescription).slice(0, 120)
                : `${cs.sof.displayScore || 'Not assessed'}: ${plainText((cs.sof.narrative || 'see report')).slice(0,100)}`,
              ok: !!(r.companyDetails?.businessDescription || (cs.sof.displayScore && cs.sof.displayScore !== 'Unsatisfactory'))
            },
            {
              q: "Where are funds coming from?",
              detail: r.sofAssessment?.namedAccount ? `${r.sofAssessment.namedAccount}${r.sofAssessment.fundingRoute ? ' · ' + r.sofAssessment.fundingRoute.slice(0,80) : ''}` : "Not confirmed",
              ok: !!(r.sofAssessment?.namedAccount)
            }
          ];

          const hasW = walletScreenings.length > 0;
          const tr = r.travelRule;
          const ubos25 = ubos25List;

          const procedural = [
            {
              q: "Identity verified (Sumsub)",
              // v1_747: N of M is the reconciled party set (Nathan deduped), never the raw verification rows.
              detail: reqPeople.length === 0 ? "No individuals listed" : reqApproved === reqPeople.length ? `${reqApproved} of ${reqPeople.length} individuals verified` : `${reqApproved} of ${reqPeople.length} verified, some pending`,
              ok: reqPeople.length === 0 || reqApproved === reqPeople.length
            },
            {
              q: "Wallet screening (Chainalysis)",
              detail: hasW ? `${walletScreenings.length} wallet(s) screened: ${walletScreenings.map(w => `${w.risk_level || 'Unknown'}`).join(', ')}` : "No crypto wallets declared",
              ok: !hasW || walletScreenings.length > 0
            },
            {
              q: "Travel Rule",
              detail: !tr || walletScreenings.length === 0 ? "No crypto wallets, not applicable" : tr.overallStatus === "compliant" ? "All wallets verified" : `${(tr.wallets || []).filter(w => w.travelRuleStatus !== "compliant").length} wallet(s) pending`,
              ok: !tr || walletScreenings.length === 0 || tr.overallStatus === "compliant"
            },
            {
              q: "UBO Source of Wealth interview",
              detail: r.sowWaiver ? `Not required: ${toStr(r.sowWaiver.reason)} (MLRO waiver)` : ubos25.length === 0 ? `No UBOs at or above the ${ownT}% threshold` : ubos25.every(sowSatisfied) ? `${ubos25.map(u => u.name).join(', ')}: interviewed and assessed` : `${ubos25.filter(u => !sowSatisfied(u)).map(u => u.name).join(', ')}: pending`,
              ok: !!r.sowWaiver || ubos25.length === 0 || ubos25.every(sowSatisfied)
            }
          ];

          const allOk = sixQuestions.every(c => c.ok) && procedural.every(p => p.ok);
          const totalDone = [...sixQuestions, ...procedural].filter(c => c.ok).length;
          const total = sixQuestions.length + procedural.length;

          return (
            <div style={{ ...S.section, marginBottom: 20 }}>
              <div style={S.sectionTitle}>What the MLRO has established</div>
              <div style={{ ...S.para, marginBottom: 14 }}>
                The following summarises how the MLRO has addressed the key due diligence questions required under {fw.citation}, together with the procedural requirements. Each item states the evidence on file.
              </div>
              <div style={{ background: allOk ? "#f0fdf4" : "#fff7ed", border: `1px solid ${allOk ? "#86efac" : "#fed7aa"}`, borderRadius: 8, padding: "10px 14px", marginBottom: 14 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: allOk ? "#15803d" : "#d97706", fontFamily: "Arial, sans-serif" }}>
                  {allOk ? `✓ All ${total} items addressed, file is complete` : `⚠ ${totalDone} of ${total} items addressed, see details below`}
                </div>
              </div>

              {/* Six Questions */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#1e2d5a", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 8, fontFamily: "Arial, sans-serif" }}>Six Key Questions</div>
                <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden" }}>
                  <thead>
                    <tr style={{ background: "#1e2d5a" }}>
                      <th style={{ padding: "8px 12px", textAlign: "left", fontSize: 11, color: "white", fontFamily: "Arial, sans-serif", fontWeight: 700, width: 220 }}>Question</th>
                      <th style={{ padding: "8px 12px", textAlign: "center", fontSize: 11, color: "white", fontFamily: "Arial, sans-serif", fontWeight: 700, width: 50 }}>Status</th>
                      <th style={{ padding: "8px 12px", textAlign: "left", fontSize: 11, color: "white", fontFamily: "Arial, sans-serif", fontWeight: 700 }}>Evidence / MLRO Finding</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sixQuestions.map((c, i) => (
                      <tr key={i} style={{ background: i % 2 === 0 ? "#fff" : "#f9fafb", borderBottom: "1px solid #f3f4f6" }}>
                        <td style={{ padding: "8px 12px", fontSize: 13, color: "#111827", fontFamily: "Arial, sans-serif", fontWeight: 600 }}>{c.q}</td>
                        <td style={{ padding: "8px 12px", textAlign: "center", fontSize: 16 }}>{c.ok ? "✅" : "⚠️"}</td>
                        <td style={{ padding: "8px 12px", fontSize: 12, color: c.ok ? "#374151" : "#d97706", fontFamily: "Arial, sans-serif" }}>{plainText(c.detail)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Procedural */}
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#1e2d5a", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 8, fontFamily: "Arial, sans-serif" }}>Procedural Requirements</div>
                <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden" }}>
                  <thead>
                    <tr style={{ background: "#1e2d5a" }}>
                      <th style={{ padding: "8px 12px", textAlign: "left", fontSize: 11, color: "white", fontFamily: "Arial, sans-serif", fontWeight: 700, width: 220 }}>Requirement</th>
                      <th style={{ padding: "8px 12px", textAlign: "center", fontSize: 11, color: "white", fontFamily: "Arial, sans-serif", fontWeight: 700, width: 50 }}>Status</th>
                      <th style={{ padding: "8px 12px", textAlign: "left", fontSize: 11, color: "white", fontFamily: "Arial, sans-serif", fontWeight: 700 }}>Detail</th>
                    </tr>
                  </thead>
                  <tbody>
                    {procedural.map((c, i) => (
                      <tr key={i} style={{ background: i % 2 === 0 ? "#fff" : "#f9fafb", borderBottom: "1px solid #f3f4f6" }}>
                        <td style={{ padding: "8px 12px", fontSize: 13, color: "#111827", fontFamily: "Arial, sans-serif", fontWeight: 600 }}>{c.q}</td>
                        <td style={{ padding: "8px 12px", textAlign: "center", fontSize: 16 }}>{c.ok ? "✅" : "⚠️"}</td>
                        <td style={{ padding: "8px 12px", fontSize: 12, color: c.ok ? "#374151" : "#d97706", fontFamily: "Arial, sans-serif" }}>{plainText(c.detail)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })()}

        {/* 1. Client Profile */}
        <div style={S.section}>
          <div style={S.sectionTitle}>1. Client Profile</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 24px" }}>
            {[
              ["Legal Name", cd.legalName || session?.company_name],
              ["Jurisdiction", cd.jurisdiction || report.jurisdiction],
              ["Registration No.", cd.registrationNumber || report.registrationNumber],
              ["Incorporated", cd.dateOfIncorporation],
              ["Registered Address", cd.registeredAddress || report.registeredAddress],
              ["Business", cd.businessDescription || report.businessDescription],
            ].filter(([,v]) => v).map(([label, val]) => (
              <div key={label} style={S.kv}>
                <span style={S.kvLabel}>{label}</span>
                <span style={S.kvVal}>{toStr(val)}</span>
              </div>
            ))}
          </div>
          {(cd.groupStructure || report.groupStructure) && <div style={{ marginTop: 12, ...S.para }}><strong>Group structure:</strong> {toStr(cd.groupStructure || report.groupStructure)}</div>}
        </div>

        {/* 2. Risk & Classification */}
        <div style={S.section}>
          <div style={S.sectionTitle}>2. Risk Assessment & Classification</div>
          <div style={{ display: "flex", gap: 24, marginBottom: 16, flexWrap: "wrap" }}>
            <div style={{ padding: "12px 20px", background: "#f9fafb", borderRadius: 8, border: "1px solid #e5e7eb" }}>
              <div style={{ fontSize: 11, color: "#9ca3af", fontFamily: "Arial, sans-serif", marginBottom: 4 }}>Risk Tier</div>
              <span style={S.badge(riskColor)}>{tierNow || ASSESSMENT_PENDING}</span>
            </div>
            {finsaCategory !== "Not classified" && (
              <div style={{ padding: "12px 20px", background: "#f9fafb", borderRadius: 8, border: "1px solid #e5e7eb" }}>
                <div style={{ fontSize: 11, color: "#9ca3af", fontFamily: "Arial, sans-serif", marginBottom: 4 }}>FinSA Classification</div>
                <span style={S.badge("#1e2d5a")}>{finsaCategory}</span>
              </div>
            )}
            <div style={{ padding: "12px 20px", background: "#f9fafb", borderRadius: 8, border: "1px solid #e5e7eb" }}>
              <div style={{ fontSize: 11, color: "#9ca3af", fontFamily: "Arial, sans-serif", marginBottom: 4 }}>Review Period</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: "#111827", fontFamily: "Arial, sans-serif" }}>{reviewPeriodShown(report, { approved: !!(session?.approved_at || session?.mlro_decision === 'Approve') })}</div>
            </div>
          </div>
          {finsaBasis && <div style={S.para}><strong>FinSA basis:</strong> {toStr(finsaBasis)}</div>}
          {(typeof fc === "object" && fc.assessment) && <div style={S.para}>{toStr(fc.assessment)}</div>}
          {report.riskRationale && <div style={{ ...S.para, marginTop: 8 }}>{toStr(report.riskRationale)}</div>}
        </div>

        {/* VQF compliance documents — reviewer-accessible via the review token */}
        {showVqfProfiles && (
          <div style={S.section}>
            <div style={S.sectionTitle}>VQF Compliance Documents</div>
            <div style={{ ...S.para, marginBottom: 12 }}>Generate the VQF format compliance documents for this case. Each opens as a PDF populated from the file.</div>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              {[["Risk Profile (902.4)", "generate-vqf-risk-profile", "VQF-Risk-Profile.pdf"], ["Customer Profile (902.5)", "generate-vqf-customer-profile", "VQF-Customer-Profile.pdf"]].map(([label, route, fallback]) => (
                <button key={route} onClick={async (ev) => {
                  const btn = ev.currentTarget; const orig = btn.textContent; btn.disabled = true; btn.textContent = "Generating\u2026"; btn.style.opacity = "0.6";
                  try {
                    const payload = review?.token ? { token: review.token } : { sessionId: session.id };
                    const r = await fetch("/api/" + route, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
                    const j = await r.json().catch(() => ({}));
                    if (!j.base64) { alert("Could not generate the document: " + (j.error || "Unknown error")); return; }
                    const link = document.createElement("a"); link.href = "data:application/pdf;base64," + j.base64; link.download = j.filename || fallback; link.click();
                  } catch (e) { alert("Error: " + (e.message || e)); }
                  finally { btn.disabled = false; btn.textContent = orig; btn.style.opacity = "1"; }
                }} style={{ padding: "10px 18px", background: "#1e2d5a", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "Arial, sans-serif" }}>{label}</button>
              ))}
            </div>
          </div>
        )}

        {/* 3. Beneficial Ownership */}
        <div style={S.section}>
          <div style={S.sectionTitle}>3. Beneficial Ownership & Identity Verification</div>
          {(report.ubos || []).map((ubo, i) => {
            const verifMatch = verifFor(ubo.name);
            return (
              <div key={i} style={{ padding: "14px 16px", background: "#f9fafb", borderRadius: 8, marginBottom: 10, border: "1px solid #e5e7eb" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: "#111827", fontFamily: "Arial, sans-serif" }}>{ubo.name}</div>
                    <div style={{ fontSize: 12, color: "#6b7280", fontFamily: "Arial, sans-serif", marginTop: 2 }}>{plainText(ubo.ownership)} · {plainText(ubo.nationality)}{ubo.countryOfResidence ? " · Resident: " + plainText(ubo.countryOfResidence) : ""}</div>
                  </div>
                  <span style={S.badge(parseFloat(ubo.ownership) < ownT ? "#6b7280" : ubo.corporateUBO ? "#7c3aed" : "#1e2d5a")}>{parseFloat(ubo.ownership) < ownT ? `Shareholder (below ${ownT}%)` : ubo.corporateUBO ? "Corporate UBO" : "Beneficial Owner"}</span>
                </div>
                {/* Identity verification */}
                <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                  <div style={{ padding: "8px 12px", background: "#fff", borderRadius: 6, border: "1px solid #e5e7eb", flex: 1, minWidth: 160 }}>
                    <div style={{ fontSize: 10, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.5px", fontFamily: "Arial, sans-serif", marginBottom: 4 }}>ID Verification (Sumsub)</div>
                    {verifMatch ? (
                      <div>
                        <span style={S.badge(verifMatch.status === "approved" ? "#15803d" : verifMatch.status === "pending" ? "#d97706" : "#dc2626")}>
                          {verifMatch.status === "approved" ? "✓ Verified" : verifMatch.status === "pending" ? "⏳ Pending" : "✗ " + verifMatch.status}
                        </span>
                        {verifMatch.applicant_id && <div style={{ fontSize: 10, color: "#9ca3af", fontFamily: "Arial, sans-serif", marginTop: 4 }}>Ref: {verifMatch.applicant_id}</div>}
                      </div>
                    ) : (
                      <span style={S.badge("#9ca3af")}>Pending</span>
                    )}
                    {REPORT_STATUSES.includes(verifMatch?.status) && (
                      <button onClick={withPressFeedback(() => openSumsubReport(verifMatch))} style={{ marginTop: 8, padding: "5px 12px", background: "#1e2d5a", color: "#fff", border: "none", borderRadius: 5, fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "Arial, sans-serif" }}>View Sumsub report</button>
                    )}
                  </div>
                  <div style={{ padding: "8px 12px", background: "#fff", borderRadius: 6, border: "1px solid #e5e7eb", flex: 1, minWidth: 160 }}>
                    <div style={{ fontSize: 10, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.5px", fontFamily: "Arial, sans-serif", marginBottom: 4 }}>PEP / Sanctions</div>
                    {(verifMatch?.pep_match || verifMatch?.sanctions_match || verifMatch?.adverse_media) ? (
                      <span>
                        {verifMatch.sanctions_match && <span style={{ ...S.badge("#dc2626"), marginRight: 4 }}>⚠ Sanctions</span>}
                        {verifMatch.pep_match && <span style={{ ...S.badge("#d97706"), marginRight: 4 }}>⚠ PEP</span>}
                        {verifMatch.adverse_media && <span style={S.badge("#d97706")}>⚠ Adverse media</span>}
                      </span>
                    ) : verifMatch?.status === "approved" ? (
                      <span style={S.badge("#15803d")}>✓ Clear</span>
                    ) : (
                      <span style={S.badge("#9ca3af")}>Pending</span>
                    )}
                    {REPORT_STATUSES.includes(verifMatch?.status) && (
                      <button onClick={withPressFeedback(() => openAmlReport(verifMatch, ubo.name, ubo.corporateUBO ? "Corporate UBO" : "Individual UBO"))} style={{ marginTop: 8, padding: "5px 12px", background: "#fff", color: "#1e2d5a", border: "1px solid #1e2d5a", borderRadius: 5, fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "Arial, sans-serif" }}>View AML screening report</button>
                    )}
                  </div>
                  {ubo.controlBeyondOwnership !== undefined && (
                    <div style={{ padding: "8px 12px", background: "#fff", borderRadius: 6, border: "1px solid #e5e7eb", flex: 1, minWidth: 160 }}>
                      <div style={{ fontSize: 10, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.5px", fontFamily: "Arial, sans-serif", marginBottom: 4 }}>Control beyond ownership</div>
                      <span style={S.badge(ubo.controlBeyondOwnership ? "#d97706" : "#15803d")}>{ubo.controlBeyondOwnership ? "Yes" : "No"}</span>
                      {ubo.controlNotes && <div style={{ fontSize: 11, color: "#6b7280", fontFamily: "Arial, sans-serif", marginTop: 4 }}>{toStr(ubo.controlNotes)}</div>}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          {(() => {
            const people = reqPeople.length > 0 ? reqPeople
              : (report.directors || []).map(d => (typeof d === "object" ? { name: d.name, role: d.role || "Director" } : { name: d, role: "Director" }));
            if (people.length === 0) return null;
            return (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 11, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 8, fontFamily: "Arial, sans-serif", fontWeight: 700 }}>Directors, signatories &amp; key persons: ID &amp; screening</div>
                {people.map((p, i) => {
                  const v = verifFor(p.name);
                  const ok = v?.status === "approved";
                  const flagged = v && (v.pep_match || v.sanctions_match || v.adverse_media);
                  return (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 10px", background: "#f9fafb", borderRadius: 6, marginBottom: 4, flexWrap: "wrap" }}>
                      <div style={{ flex: 1, minWidth: 180 }}>
                        <span style={{ fontSize: 13, color: "#111827", fontFamily: "Arial, sans-serif", fontWeight: 500 }}>{toStr(p.name)}</span>
                        <span style={{ fontSize: 12, color: "#6b7280", fontFamily: "Arial, sans-serif", marginLeft: 8 }}>{toStr(p.role)}</span>
                      </div>
                      <span style={S.badge(ok ? "#15803d" : v ? "#d97706" : "#9ca3af")}>{ok ? "✓ Verified" : v ? "⏳ " + (v.status || "Pending") : "Pending"}</span>
                      {flagged ? (
                        <span>
                          {v.sanctions_match && <span style={{ ...S.badge("#dc2626"), marginLeft: 4 }}>⚠ Sanctions</span>}
                          {v.pep_match && <span style={{ ...S.badge("#d97706"), marginLeft: 4 }}>⚠ PEP</span>}
                          {v.adverse_media && <span style={{ ...S.badge("#d97706"), marginLeft: 4 }}>⚠ Adverse media</span>}
                        </span>
                      ) : ok ? <span style={{ ...S.badge("#15803d"), marginLeft: 4 }}>PEP/Sanctions clear</span> : null}
                      {REPORT_STATUSES.includes(v?.status) && (
                        <span style={{ display: "inline-flex", gap: 6, marginLeft: "auto" }}>
                          <button onClick={withPressFeedback(() => openSumsubReport(v))} style={{ padding: "4px 10px", background: "#1e2d5a", color: "#fff", border: "none", borderRadius: 5, fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "Arial, sans-serif" }}>Sumsub report</button>
                          <button onClick={withPressFeedback(() => openAmlReport(v, p.name, p.role))} style={{ padding: "4px 10px", background: "#fff", color: "#1e2d5a", border: "1px solid #1e2d5a", borderRadius: 5, fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "Arial, sans-serif" }}>AML report</button>
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })()}

          {/* Entity sanctions screening — OpenSanctions */}
          {entitySanctions && (() => {
            const st = entitySanctions.status;
            const d = entitySanctions.data || {};
            const matches = [...(d.hits || []).map(h => ({ ...h, level: "hit" })), ...(d.cautionMatches || []).map(h => ({ ...h, level: "caution" }))];
            const dismissed = d.dismissed || {};
            const head = st === "hit" ? ["#dc2626", "⚠ Sanctions hit"] : st === "caution" ? ["#d97706", "◈ Partial match"]
              : (st === "clear" || st === "clear_with_notes") ? ["#15803d", st === "clear_with_notes" ? "✓ Clear (false positives noted)" : "✓ Clear"]
              : ["#9ca3af", st === "running" ? "Screening…" : st === "no_key" ? "Not run" : "Not yet run"];
            return (
              <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid #e5e7eb" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, flexWrap: "wrap", gap: 8 }}>
                  <div>
                    <div style={{ fontSize: 11, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.5px", fontFamily: "Arial, sans-serif", fontWeight: 700 }}>Entity Sanctions Screening (OpenSanctions)</div>
                    <div style={{ fontSize: 11, color: "#9ca3af", fontFamily: "Arial, sans-serif", marginTop: 2 }}>OFAC · EU · UK HMT · UN · 200+ lists · company entity (individuals screened via Sumsub above)</div>
                  </div>
                  <span style={S.badge(head[0])}>{head[1]}</span>
                </div>
                {(st === "clear" || st === "clear_with_notes") && (
                  <div style={{ fontSize: 12, color: "#374151", fontFamily: "Arial, sans-serif", lineHeight: 1.6 }}>
                    No sanctions match for <strong>{toStr(d.entityChecked) || toStr(cd.legalName)}</strong>{d.listsSearched ? " · " + toStr(d.listsSearched) : ""}{d.checkedAt ? " · screened " + new Date(d.checkedAt).toLocaleDateString("en-GB") : ""}.
                  </div>
                )}
                {(st === "hit" || st === "caution") && matches.map((h, i) => {
                  const dis = dismissed[h.name];
                  return (
                    <div key={i} style={{ background: dis ? "#f0fdf4" : h.level === "hit" ? "#fff1f2" : "#fff7ed", border: "1px solid " + (dis ? "#bbf7d0" : h.level === "hit" ? "#fecaca" : "#fed7aa"), borderRadius: 6, padding: "8px 12px", marginBottom: 6 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: dis ? "#6b7280" : h.level === "hit" ? "#991b1b" : "#92400e", textDecoration: dis ? "line-through" : "none", fontFamily: "Arial, sans-serif" }}>{toStr(h.name)}: match {h.score}%</div>
                      <div style={{ fontSize: 11, color: "#6b7280", fontFamily: "Arial, sans-serif" }}>Lists: {(h.datasets || []).join(", ")}{h.countries ? " · " + (Array.isArray(h.countries) ? h.countries.join(", ") : h.countries) : ""}</div>
                      {dis && <div style={{ fontSize: 11, color: "#15803d", fontFamily: "Arial, sans-serif", marginTop: 4, paddingLeft: 8, borderLeft: "2px solid #bbf7d0" }}>✓ False positive: {toStr(dis.dismissedBy)} {dis.dismissedAt ? new Date(dis.dismissedAt).toLocaleDateString("en-GB") : ""}: {toStr(dis.reason)}</div>}
                    </div>
                  );
                })}
              </div>
            );
          })()}

          {/* Per-director/UBO OpenSanctions results (v1_645). Each individual as its own row,
              an independent OpenSanctions record distinct from the identity-verification step. */}
          {(() => {
            const rows = partyScreeningRows(report, session?.collected_data?.partyScreenings, session?.collected_data?.screeningResolutions);
            if (!rows.length) return null;
            const col = (s) => s === "hit" ? "#dc2626" : s === "caution" ? "#d97706" : s === "clear" ? "#15803d" : "#9ca3af";
            return (
              <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid #e5e7eb" }}>
                <div style={{ fontSize: 11, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.5px", fontFamily: "Arial, sans-serif", fontWeight: 700, marginBottom: 2 }}>Individuals (OpenSanctions Screening)</div>
                <div style={{ fontSize: 11, color: "#9ca3af", fontFamily: "Arial, sans-serif", marginBottom: 8 }}>Directors and beneficial owners screened at analysis (OpenSanctions). An independent record. Identity verification is a separate step.</div>
                {rows.map((pr, i) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, padding: "6px 0", borderBottom: i < rows.length - 1 ? "1px solid #f3f4f6" : "none" }}>
                    <div><span style={{ fontSize: 12, fontWeight: 600, color: "#374151", fontFamily: "Arial, sans-serif" }}>{toStr(pr.name)}</span> <span style={{ fontSize: 11, color: "#9ca3af", fontFamily: "Arial, sans-serif" }}>· {pr.role}{pr.checkedAt ? " · " + new Date(pr.checkedAt).toLocaleDateString("en-GB") : ""}</span></div>
                    <span style={{ fontSize: 12, fontWeight: 600, color: col(pr.status), fontFamily: "Arial, sans-serif", whiteSpace: "nowrap" }}>{PARTY_STATUS_LABEL[pr.status] || pr.status}</span>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>

        {/* Online identification of the CONTRACTING PARTY (FINMA Circular 2016/7). The report had
            no line for this at all: identity verification of the individuals was shown, but the
            additional confirming element that completes online identification of the company —
            a confirmation payment from an account in the contracting party's own name, or video
            or NFC identification — appeared nowhere. A VQF reviewer cannot conclude the entity was
            properly identified without it. Only rendered for VQF firms, where the requirement bites. */}
        {showOnlineIdentification && (
          <div style={S.section}>
            <div style={S.sectionTitle}>Online Identification: Contracting Party (VQF)</div>
            <div style={{ fontSize: 11, color: "#6b7280", fontFamily: "Arial, sans-serif", marginBottom: 12 }}>
              FINMA Circular 2016/7. {(() => { const ev = verifFor((session?.collected_data?.establishingPerson && session.collected_data.establishingPerson.name) || session?.contact_name); return ev && verificationMethod(ev) === 'documents' ? ONLINE_ID_DOCS_WORDS : 'Document authenticity, liveness and proof of address for the establishing person are covered by the identity verification above.'; })()} Online identification is completed by an additional confirming element: a confirmation payment from an account held in the contracting party's own name at a bank in an FATF member jurisdiction, or video or NFC identification.
            </div>
            {(() => {
              const method = session?.vqf_id_method || (session?.vqf_payment_ref ? "online_payment" : null);
              const status = session?.vqf_payment_status || null;
              const methodLabel = method === "video" ? "Video identification"
                : method === "nfc" ? "NFC chip identification"
                : method === "online_payment" ? "Confirmation payment (penny test)"
                : "Not yet selected";
              const done = method === "online_payment" ? status === "received" : (method === "video" || method === "nfc");
              return (
                <div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#111827", fontFamily: "Arial, sans-serif" }}>{methodLabel}</div>
                    <span style={S.badge(done ? "#15803d" : "#d97706")}>{done ? "✓ Completed" : method ? "◐ Outstanding" : "Not started"}</span>
                  </div>
                  {/* v1_859: an establishing person the MLRO designated, with the one it replaced. */}
                  {session?.collected_data?.establishingPerson?.name && (() => {
                    const d = session.collected_data.establishingPerson; const p = d.previous || {};
                    return (
                      <div style={S.kv} data-est-designated><span style={S.kvLabel}>Establishing Person</span><span style={S.kvVal}>{toStr(d.name)}{d.role ? " (" + toStr(d.role) + ")" : ""}, designated by {toStr(d.designatedBy || "the MLRO")}{d.designatedAt ? " on " + new Date(d.designatedAt).toLocaleDateString("en-GB") : ""}, replacing {toStr(p.name || "the onboarding contact")} ({toStr(p.source || "the onboarding contact")}). Reason: {toStr(d.reason || "not recorded")}</span></div>
                    );
                  })()}
                  {method === "online_payment" && (
                    <div>
                      {session?.vqf_payment_ref && <div style={S.kv}><span style={S.kvLabel}>Payment Reference</span><span style={{ ...S.kvVal, fontFamily: "monospace" }}>{toStr(session.vqf_payment_ref)}{session?.vqf_payment_currency ? " · " + toStr(session.vqf_payment_currency) : ""}</span></div>}
                      {session?.vqf_payment_requested_at && <div style={S.kv}><span style={S.kvLabel}>Requested</span><span style={S.kvVal}>{new Date(session.vqf_payment_requested_at).toLocaleString("en-GB")}</span></div>}
                      {session?.vqf_payment_received_at && <div style={S.kv}><span style={S.kvLabel}>Received</span><span style={S.kvVal}>{new Date(session.vqf_payment_received_at).toLocaleString("en-GB")}</span></div>}
                      <div style={{ ...S.para, marginTop: 8, color: done ? "#15803d" : "#b45309" }}>
                        {done
                          ? "A confirmation payment was received from an account in the contracting party's name, matched by sender name and reference. The incoming transfer record is retained on the case file as the evidence of identification."
                          : "The confirmation payment has been requested but not yet reconciled. Online identification of the contracting party is NOT complete until the transfer is received and matched by sender name and reference."}
                      </div>
                    </div>
                  )}
                  {(method === "video" || method === "nfc") && (
                    <div style={{ ...S.para, marginTop: 4 }}>Identification was completed by {method === "video" ? "video identification" : "NFC chip reading"} rather than a confirmation payment. The identification record is retained on the case file.</div>
                  )}
                  {!method && (
                    <div style={{ ...S.para, marginTop: 4, color: "#b45309" }}>
                      No identification method has been recorded for the contracting party. Online identification under FINMA Circular 2016/7 is incomplete.
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        )}

        {/* Form A / K detail — the table above collapses every declaration into one line
            ("Completed: Form K (Sumsub OTP)"). A second signer needs to see WHO declared,
            which form, by which route and when, per signer. */}
        {(() => {
          const rows = [
            ...(deepsignRequests || []).filter(r => (r.form_type === "A" || r.form_type === "K") && r.status !== "cancelled")
              .map(r => ({ form: r.form_type, signer: r.signer_name, email: r.signer_email, route: "DeepSign QES", done: r.status === "signed", when: r.signed_at || r.updated_at || r.created_at, status: r.status })),
            ...(sumsubFormRequests || []).filter(r => (r.form_type === "A" || r.form_type === "K") && r.status !== "cancelled")
              .map(r => ({ form: r.form_type, signer: r.signer_name, email: r.signer_email, route: "Sumsub OTP", done: r.status === "confirmed", when: r.confirmed_at || r.updated_at || r.created_at, status: r.status, applicantId: r.applicant_id, record: r }))
          ];
          if (!rows.length || !showSwissInstruments) return null;
          return (
            <div style={S.section}>
              <div style={S.sectionTitle}>Form A / Form K Declarations</div>
              <div style={{ fontSize: 11, color: "#6b7280", fontFamily: "Arial, sans-serif", marginBottom: 10 }}>
                Beneficial ownership (Form A) and controlling person (Form K) declarations, per signer. Confirmation is by qualified electronic signature or by Sumsub questionnaire with one time password confirmation; the declaration content is retained on the case file.
              </div>
              {rows.map((r, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, padding: "8px 12px", background: i % 2 === 0 ? "#f9fafb" : "#fff", border: "1px solid #e5e7eb", borderRadius: 6, marginBottom: 5 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "#111827", fontFamily: "Arial, sans-serif" }}>Form {toStr(r.form)}: {toStr(r.signer) || "signer not recorded"}</div>
                    <div style={{ fontSize: 11, color: "#6b7280", fontFamily: "Arial, sans-serif" }}>{toStr(r.email)}{r.email ? " · " : ""}{toStr(r.route)}{r.when ? " · " + new Date(r.when).toLocaleDateString("en-GB") : ""}</div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                    <span style={S.badge(r.done ? "#15803d" : "#d97706")}>{r.done ? "✓ Confirmed" : "◐ " + (toStr(r.status) || "pending")}</span>
                    {r.done && r.applicantId && (
                      <button onClick={() => openFormView(r)} style={{ padding: "4px 12px", background: "transparent", color: "#1e2d5a", border: "1px solid #1e2d5a", borderRadius: 4, fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "Arial, sans-serif" }}>View</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          );
        })()}

        {/* Applicant authority to act — the whole applicantAuthority object was absent from this
            report, while the dashboard shows it as a blocking red panel. A second signer must see
            who submitted the application, on what basis they claim authority, and whether that is
            documented or merely attested. */}
        {report.applicantAuthority && (report.applicantAuthority.personName || report.applicantAuthority.authorityConfirmed !== undefined) && (
          <div style={S.section}>
            <div style={S.sectionTitle}>Authority to Act</div>
            {(() => {
              const aa = report.applicantAuthority || {};
              const confirmed = aa.authorityConfirmed === true;
              return (
                <div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#111827", fontFamily: "Arial, sans-serif" }}>{toStr(aa.personName) || toStr(report.contactName) || "Applicant"}</div>
                    <span style={S.badge(confirmed ? "#15803d" : "#dc2626")}>{confirmed ? "✓ Confirmed" : "Not confirmed"}</span>
                  </div>
                  {aa.roleAtEntity && <div style={S.kv}><span style={S.kvLabel}>Role at Entity</span><span style={S.kvVal}>{toStr(aa.roleAtEntity)}</span></div>}
                  {aa.capacity && <div style={S.kv}><span style={S.kvLabel}>Capacity</span><span style={S.kvVal}>{toStr(aa.capacity)}</span></div>}
                  {aa.authorityBasis && <div style={S.kv}><span style={S.kvLabel}>Basis of Authority</span><span style={S.kvVal}>{toStr(aa.authorityBasis)}</span></div>}
                  <div style={S.kv}><span style={S.kvLabel}>Evidence</span><span style={S.kvVal}>{aa.evidenceAvailable ? "Documentary evidence on file" : "Applicant attestation only, not documentary verification"}</span></div>
                  {aa.authorityHolderName && <div style={S.kv}><span style={S.kvLabel}>Authority Rests With</span><span style={S.kvVal}>{toStr(aa.authorityHolderName)}{aa.authorityHolderEmail ? " · " + toStr(aa.authorityHolderEmail) : ""}</span></div>}
                  {aa.declinedReason && <div style={{ ...S.para, marginTop: 8, color: "#b45309" }}>{toStr(aa.declinedReason)}</div>}
                </div>
              );
            })()}
          </div>
        )}

        {/* VQF / AMLA client profile — estimated assets and liabilities, origin-of-assets category,
            introducer and the source of the financial figures. Absent from this report entirely,
            despite being the Form A/K profile data a VQF-supervised signer needs. */}
        {report.vqfProfile && Object.values(report.vqfProfile).some(v => v && String(v).trim() && String(v) !== "...") && (
          <div style={S.section}>
            <div style={S.sectionTitle}>Client Financial Profile (VQF / AMLA)</div>
            {report.vqfProfile.estimatedAssets && <div style={S.kv}><span style={S.kvLabel}>Estimated Assets</span><span style={S.kvVal}>{toStr(report.vqfProfile.estimatedAssets)}</span></div>}
            {report.vqfProfile.estimatedLiabilities && <div style={S.kv}><span style={S.kvLabel}>Estimated Liabilities</span><span style={S.kvVal}>{toStr(report.vqfProfile.estimatedLiabilities)}</span></div>}
            {report.vqfProfile.assetOriginCategory && <div style={S.kv}><span style={S.kvLabel}>Origin of Assets</span><span style={S.kvVal}>{toStr(report.vqfProfile.assetOriginCategory)}</span></div>}
            <div style={S.kv}><span style={S.kvLabel}>Introducer</span><span style={S.kvVal}>{toStr(report.vqfProfile.introducer) || "Not stated"}</span></div>
            {report.vqfProfile.financialsSource && <div style={S.kv}><span style={S.kvLabel}>Source of Figures</span><span style={S.kvVal}>{toStr(report.vqfProfile.financialsSource)}</span></div>}
          </div>
        )}

        {/* Authority to Act (v1_466): whether and how the completer's authority
            was evidenced. Confirmation shows the full chain — director, address,
            code-verified access, date and time, method — because "a director
            confirmed it" is only signable when the signatory can see how. */}
        {attestations.length > 0 && (
          <div style={S.section}>
            <div style={S.sectionTitle}>Authority to Act: Director Attestation</div>
            <div style={{ fontSize: 12, color: "#4b5563", lineHeight: 1.7, marginBottom: 12 }}>
              Confirmation that the person completing this onboarding is authorised to act for the company (MLR 2017 reg 28(10)), given by a named director through a link verified by code. Each request below shows its full chain; a confirmed attestation also files a Confirmation of Authority in the supporting documents.
            </div>
            {attestations.map((a, i) => {
              const expired = a.status === "pending" && a.expires_at && new Date(a.expires_at) < new Date();
              const stateLabel = a.status === "confirmed" ? "CONFIRMED" : a.status === "declined" ? "DECLINED" : a.status === "withdrawn" ? "Withdrawn" : a.status === "superseded" ? "Superseded" : expired ? "Expired unanswered" : "Pending";
              const stateColor = a.status === "confirmed" ? "#0f766e" : a.status === "declined" ? "#b91c1c" : "#6b7280";
              return (
                <div key={a.id || i} style={{ border: "1px solid #e5e7eb", borderRadius: 8, padding: "12px 16px", marginBottom: 10, background: a.status === "confirmed" ? "#f0fdfa" : a.status === "declined" ? "#fef2f2" : "#fafafa" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>{a.director_name}</span>
                    <span style={{ fontSize: 11.5, fontWeight: 700, color: stateColor, letterSpacing: "0.5px" }}>{stateLabel}</span>
                  </div>
                  <div style={S.kv}><span style={S.kvLabel}>Requested</span><span style={S.kvVal}>{a.created_at ? new Date(a.created_at).toLocaleString("en-GB") : "not recorded"} by {a.created_by === "Auto" ? "the platform at submission" : a.created_by}</span></div>
                  <div style={S.kv}><span style={S.kvLabel}>Sent to</span><span style={S.kvVal}>{a.director_email}</span></div>
                  <div style={S.kv}><span style={S.kvLabel}>Method</span><span style={S.kvVal}>Secure link; access verified by a six digit code emailed to the address above</span></div>
                  {a.decided_at && <div style={S.kv}><span style={S.kvLabel}>{a.status === "confirmed" ? "Confirmed" : "Answered"}</span><span style={S.kvVal}>{new Date(a.decided_at).toLocaleString("en-GB")}</span></div>}
                  {a.decision_note && <div style={S.kv}><span style={S.kvLabel}>Director's note</span><span style={S.kvVal}>{a.decision_note}</span></div>}
                  {a.status === "confirmed" && <div style={S.kv}><span style={S.kvLabel}>Record</span><span style={S.kvVal}>Confirmation of Authority filed in Supporting Documents (reference {String(a.id || "").slice(0, 8)})</span></div>}
                  {a.domain_mismatch && <div style={{ fontSize: 11.5, color: "#92400e", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 6, padding: "7px 10px", marginTop: 8, lineHeight: 1.6 }}>The director's email domain matches neither the company website nor the completer's domain. The address was supplied by the completer; weigh accordingly.</div>}
                </div>
              );
            })}
          </div>
        )}

        {/* Registry Verification (v1_471): what the official register said,
            and how it reconciles with the file. Verdicts and named people;
            the payloads live on the case. */}
        {registryChecks.length > 0 && (
          <div style={S.section}>
            <div style={S.sectionTitle}>Registry Verification: Official Register</div>
            <div style={{ fontSize: 12, color: "#4b5563", lineHeight: 1.7, marginBottom: 12 }}>
              Company details, officers and ownership retrieved live from the official company register (Kyckr) and checked deterministically against the file. Every check records its source correlation reference and its cost on the case.
            </div>
            {registryChecks.map((c, i) => {
              const ccx = c.cross_check || {};
              const rcx = c.reconciliation || null;
              const hard = ccx.registryInactive;
              return (
                <div key={c.id || i} style={{ border: "1px solid #e5e7eb", borderRadius: 8, padding: "12px 16px", marginBottom: 10, background: hard ? "#fef2f2" : "#fafafa" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>{c.company_name || c.kyckr_id} · {c.product === "enhanced" ? "Enhanced Profile" : "Lite Profile"}</span>
                    <span style={{ fontSize: 11.5, fontWeight: 700, color: hard ? "#b91c1c" : ccx.verdict === "match" ? "#0f766e" : ccx.verdict === "mismatch" ? "#b45309" : "#6b7280", letterSpacing: "0.5px" }}>{hard ? "NOT ACTIVE AT REGISTER" : ccx.verdict === "match" ? "CORROBORATED" : ccx.verdict === "mismatch" ? "MISMATCH: " + (ccx.mismatches || []).join(", ").toUpperCase() : "CHECKED"}</span>
                  </div>
                  <div style={S.kv}><span style={S.kvLabel}>Checked</span><span style={S.kvVal}>{c.created_at ? new Date(c.created_at).toLocaleString("en-GB") : "not recorded"} by {c.requested_by || "officer"} · source ref {c.correlation_id ? String(c.correlation_id).slice(0, 12) : "n/a"}</span></div>
                  {rcx && (rcx.registryConfirmed || []).length > 0 && <div style={S.kv}><span style={S.kvLabel}>Confirmed at register</span><span style={S.kvVal}>{rcx.registryConfirmed.map(x => `${x.name}${x.role ? " (" + x.role + ")" : ""}`).join("; ")}</span></div>}
                  {rcx && (rcx.undisclosedAtRegistry || []).length > 0 && <div style={{ fontSize: 11.5, color: "#b91c1c", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 6, padding: "7px 10px", marginTop: 6, lineHeight: 1.6 }}>At the register but not on the file: {rcx.undisclosedAtRegistry.map(x => `${x.name}${x.role ? " (" + x.role + ")" : ""}`).join("; ")}. Requires an explanation before the declared structure can be relied upon.</div>}
                  {rcx && (rcx.claimedNotAtRegistry || []).length > 0 && <div style={S.kv}><span style={S.kvLabel}>On file, not at register</span><span style={S.kvVal}>{rcx.claimedNotAtRegistry.map(x => x.name).join("; ")}</span></div>}
                  {rcx && (rcx.ownership || []).filter(o => !o.onFile).length > 0 && <div style={S.kv}><span style={S.kvLabel}>Owners not disclosed on file</span><span style={S.kvVal}>{rcx.ownership.filter(o => !o.onFile).map(o => `${o.registryName} (${o.registryRange || (o.registryPercentage != null ? o.registryPercentage + "%" : o.source)})`).join("; ")}</span></div>}
                  {rcx && rcx.completer && <div style={S.kv}><span style={S.kvLabel}>Establishing person</span><span style={S.kvVal}>{rcx.completer.registryConfirmed ? `${rcx.completer.name} is at the register as ${rcx.completer.role || "an active officer"}; authority derives from their registered office` : `${rcx.completer.name} is not an officer at the register; authority rests on the attestation or board authority document`}</span></div>}
                </div>
              );
            })}
          </div>
        )}

        {/* 4. Source of Funds */}
        {report.sofAssessment && (
          <div style={S.section}>
            <div style={S.sectionTitle}>4. Source of Funds</div>
                        {report.sofAssessment.score && <div style={S.kv}><span style={S.kvLabel}>Assessment</span><span style={S.kvVal}>{toStr(report.sofAssessment.score)}</span></div>}
            {report.sofAssessment.namedAccount && <div style={S.kv}><span style={S.kvLabel}>Named Account</span><span style={S.kvVal}>{toStr(report.sofAssessment.namedAccount)}</span></div>}
{report.sofAssessment.fundingRoute && <div style={S.kv}><span style={S.kvLabel}>Funding Route</span><span style={S.kvVal}>{toStr(report.sofAssessment.fundingRoute)}</span></div>}
            {report.sofAssessment.expectedVolumes && <div style={S.kv}><span style={S.kvLabel}>Expected Volumes</span><span style={S.kvVal}>{toStr(report.sofAssessment.expectedVolumes)}</span></div>}
            {/* The schema field is `narrative`; `assessment` (kept for legacy reports) never
                existed on newer ones, so the substantive source-of-funds text was silently
                dropped from the signed review while the dashboard showed it in full. */}
            {report.sofAssessment.narrative && <div style={{ ...S.para, marginTop: 8 }}>{toStr(report.sofAssessment.narrative)}</div>}
            {report.sofAssessment.assessment && <div style={{ ...S.para, marginTop: 8 }}>{toStr(report.sofAssessment.assessment)}</div>}
            {report.sofAssessment.notes && <div style={{ ...S.para, marginTop: 6, fontStyle: 'italic', color: '#6b7280' }}>{toStr(report.sofAssessment.notes)}</div>}
          </div>
        )}

        {/* 5. Source of Wealth — unified section */}
        {((report.ubos || []).some(u => isPersonalSowUbo(u, caseOwnershipThreshold(report))) || sowSessions.length > 0 || report.sowAssessment?.narrative) && (
          <div style={S.section}>
            <div style={S.sectionTitle}>5. Source of Wealth: UBOs at or above {ownT}%</div>
            {/* This document is signed by a third party, so it must never assert steps that have
                not happened: the previous fixed text claimed every profile was "reviewed and
                signed off by the internal MLRO" even while an interview was still pending
                (Belmoney, 25/07/26). State what is true for the profiles actually shown. */}
            {report.sowAssessment?.narrative && (
              <div style={{ marginBottom: 14 }}>
                {report.sowAssessment.score && <div style={S.kv}><span style={S.kvLabel}>Assessment</span><span style={S.kvVal}>{String(report.sowAssessment.score)}</span></div>}
                <div style={{ ...S.para, marginTop: 6 }}>{plainText(String(report.sowAssessment.narrative))}</div>
                {report.sowAssessment.notes && <div style={{ ...S.para, marginTop: 6, fontStyle: 'italic', color: '#6b7280' }}>{plainText(String(report.sowAssessment.notes))}</div>}
              </div>
            )}
            <div style={{ ...S.para, color: "#6b7280", marginBottom: 16 }}>
              {(() => {
                const shown = (report.ubos || []).filter(u => isPersonalSowUbo(u, caseOwnershipThreshold(report)));
                const complete = shown.filter(u => u.sowNarrative || u.sowAssessment || (sowSessionFor && sowSessionFor(u)?.report));
                if (shown.length === 0) return `No natural person UBO at or above the ${ownT}% threshold has a personal source of wealth profile on this file.`;
                if (complete.length === shown.length) return 'Source of wealth profiles were collected via a structured compliance interview and reviewed by the internal MLRO.';
                return `Source of wealth profiles are collected via a structured compliance interview. ${complete.length} of ${shown.length} profile${shown.length === 1 ? '' : 's'} ${complete.length === 1 ? 'is' : 'are'} on file; the remainder are pending and this section will be incomplete until they are.`;
              })()}
            </div>
            {(report.ubos || []).filter(u => isPersonalSowUbo(u, caseOwnershipThreshold(report))).map((ubo, i) => {
              const sowSess = sowSessionFor(ubo);
              const sowReport = sowSess?.report || {};
              const isExpanded = expandedSow[ubo.name] || false;
              const assessment = ubo.sowAssessment || sowReport.sowAssessment;
              return (
                <div key={i} style={{ border: "1px solid #e5e7eb", borderRadius: 8, marginBottom: 12, overflow: "hidden" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 16px", background: "#f9fafb", cursor: "pointer" }}
                    onClick={() => setExpandedSow(prev => ({ ...prev, [ubo.name]: !prev[ubo.name] }))}>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: "#111827", fontFamily: "Arial, sans-serif" }}>{ubo.name}</div>
                      <div style={{ fontSize: 12, color: "#6b7280", fontFamily: "Arial, sans-serif", marginTop: 2 }}>{plainText(ubo.ownership)} · {plainText(ubo.nationality)}</div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      {assessment && <span style={S.badge(String(assessment).includes("Satisfactory") ? "#15803d" : String(assessment).includes("Basic") || String(assessment).includes("Clarification") ? "#d97706" : "#dc2626")}>{toStr(assessment)}</span>}
                      <span style={{ fontSize: 16, color: "#9ca3af" }}>{isExpanded ? "▲" : "▼"}</span>
                    </div>
                  </div>
                  {isExpanded && (
                    <div style={{ padding: "16px 20px" }}>
                      {/* MLRO summary narrative */}
                      {ubo.sowNarrative && (
                        <div style={{ marginBottom: 14 }}>
                          <div style={{ fontSize: 11, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.5px", fontFamily: "Arial, sans-serif", marginBottom: 6, fontWeight: 700 }}>MLRO Summary</div>
                          <div style={S.para}>{toStr(ubo.sowNarrative)}</div>
                          {ubo.sowMlroRationale && <div style={{ ...S.para, fontStyle: "italic", color: "#6b7280", marginTop: 4, paddingLeft: 12, borderLeft: "3px solid #e5e7eb" }}>{toStr(ubo.sowMlroRationale)}</div>}
                        </div>
                      )}
                      {/* Full interview detail */}
                      {sowSess && (
                        <div>
                          {sowReport.professionalBackground && (
                            <div style={{ marginBottom: 12 }}>
                              <div style={{ fontSize: 11, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.5px", fontFamily: "Arial, sans-serif", marginBottom: 5, fontWeight: 700 }}>Professional Background</div>
                              <div style={S.para}>{toStr(sowReport.professionalBackground)}</div>
                            </div>
                          )}
                          {sowReport.shareholdingOrigin && (
                            <div style={{ marginBottom: 12 }}>
                              <div style={{ fontSize: 11, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.5px", fontFamily: "Arial, sans-serif", marginBottom: 5, fontWeight: 700 }}>Shareholding Origin</div>
                              <div style={S.para}>{toStr(sowReport.shareholdingOrigin)}</div>
                            </div>
                          )}
                          {(sowReport.sowNotes || sowReport.wealthOrigins) && (
                            <div style={{ marginBottom: 12 }}>
                              <div style={{ fontSize: 11, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.5px", fontFamily: "Arial, sans-serif", marginBottom: 5, fontWeight: 700 }}>Wealth Origins</div>
                              <div style={S.para}>{toStr(sowReport.sowNotes || sowReport.wealthOrigins)}</div>
                            </div>
                          )}
                          {sowReport.significantEvents && (
                            <div style={{ marginBottom: 12 }}>
                              <div style={{ fontSize: 11, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.5px", fontFamily: "Arial, sans-serif", marginBottom: 5, fontWeight: 700 }}>Significant Events</div>
                              <div style={S.para}>{toStr(sowReport.significantEvents)}</div>
                            </div>
                          )}
                          {[["Wealth timeframe", sowReport.wealthTimeframe], ["Wealth structure", sowReport.wealthStructure]].filter(([,v]) => v).map(([label, val]) => (
                            <div key={label} style={S.kv}><span style={S.kvLabel}>{label}</span><span style={S.kvVal}>{toStr(val)}</span></div>
                          ))}
                        </div>
                      )}
                      {/* Evidence */}
                      {(ubo.evidenceProvided || sowReport.evidenceProvided) && (
                        <div style={{ padding: "10px 14px", background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 6, margin: "12px 0" }}>
                          <div style={{ fontSize: 11, color: "#0369a1", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", fontFamily: "Arial, sans-serif", marginBottom: 4 }}>Evidence Provided</div>
                          <div style={{ fontSize: 13, color: "#0c4a6e", fontFamily: "Arial, sans-serif", lineHeight: 1.6 }}>{toStr(ubo.evidenceProvided || sowReport.evidenceProvided)}</div>
                        </div>
                      )}
                      {/* SOW documents */}
                      {sowSess && (sowDocs[sowSess.id] || []).length > 0 && (
                        <div style={{ marginTop: 12 }}>
                          <div style={{ fontSize: 11, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.5px", fontFamily: "Arial, sans-serif", marginBottom: 8, fontWeight: 700 }}>Supporting Documents</div>
                          {sowDocs[sowSess.id].map((doc, di) => (
                            <div key={di} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", background: "#f9fafb", borderRadius: 6, marginBottom: 6, border: "1px solid #e5e7eb" }}>
                              <span style={{ fontSize: 18 }}>📄</span>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontSize: 13, color: "#111827", fontFamily: "Arial, sans-serif", fontWeight: 500 }}>{docName(doc)}</div>
                                {doc.assessment && <div style={{ fontSize: 11, color: doc.assessment === "Satisfactory" ? "#15803d" : "#d97706", fontFamily: "Arial, sans-serif", marginTop: 2, fontWeight: 600 }}>{doc.assessment}</div>}
                              </div>
                              {((doc.file_path && doc.file_path !== 'pending') || doc.file_url) ? (
                                <button onClick={() => openFile(doc.file_path, doc.file_url)} style={{ padding: "6px 14px", background: "#1e2d5a", color: "#fff", border: "none", borderRadius: 5, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "Arial, sans-serif" }}>View ↗</button>
                              ) : <span style={{ fontSize: 11, color: "#d1d5db" }}>No file</span>}
                            </div>
                          ))}
                        </div>
                      )}
                      {sowSess?.updated_at && <div style={{ marginTop: 10, fontSize: 11, color: "#9ca3af", fontFamily: "Arial, sans-serif" }}>Interview completed: {new Date(sowSess.updated_at).toLocaleDateString("en-GB")}</div>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* 6. Tax Compliance */}
        <div style={S.section}>
          <div style={S.sectionTitle}>6. Tax Compliance Declaration</div>
          {report.taxCompliance?.riskIndicatorsPresent ? (
            <div style={{ padding:"10px 14px", background:"#fff7ed", border:"1px solid #fed7aa", borderRadius:6 }}>
              <div style={{ fontSize:12, fontWeight:700, color:"#d97706", fontFamily:"Arial, sans-serif", marginBottom:4 }}>⚠ Risk Indicators Present</div>
              <div style={{ fontSize:12, color:"#92400e", fontFamily:"Arial, sans-serif", lineHeight:1.6 }}>{toStr(report.taxCompliance.notes)}</div>
              {(report.taxCompliance.riskIndicators || []).map((r,i) => <div key={i} style={{ fontSize:11, color:"#92400e", fontFamily:"Arial, sans-serif" }}>• {toStr(r)}</div>)}
            </div>
          ) : report.taxCompliance?.declared ? (
            <div style={{ padding:"10px 14px", background:"#f0fdf4", border:"1px solid #86efac", borderRadius:6 }}>
              <div style={{ fontSize:12, fontWeight:700, color:"#15803d", fontFamily:"Arial, sans-serif", marginBottom:4 }}>✓ Declaration Obtained</div>
              <div style={{ fontSize:12, color:"#166534", fontFamily:"Arial, sans-serif", lineHeight:1.6 }}>{report.taxCompliance.declarationText || "Client confirmed assets are fully tax compliant in all relevant jurisdictions."}</div>
            </div>
          ) : (
            <div style={{ ...S.para, color:"#9ca3af" }}>Tax compliance declaration not yet recorded.</div>
          )}
          <div style={{ fontSize:11, color:"#9ca3af", fontFamily:"Arial, sans-serif", marginTop:8 }}>Required under applicable AML regulations</div>
        </div>

        {/* 7. Documents */}
        <div style={S.section}>
          <div style={S.sectionTitle}>7. Documents on File ({documents.length})</div>
          {documents.length === 0 ? <div style={{ ...S.para, color: "#9ca3af" }}>No documents uploaded</div> : (
            <div>
              {documents.map((doc, i) => (
                <div key={i} style={{ padding:"12px 14px", borderRadius:6, border:"1px solid #e5e7eb", marginBottom:8, background: i % 2 === 0 ? "#fff" : "#fafafa" }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:12 }}>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:13, fontWeight:600, color:"#111827", fontFamily:"Arial, sans-serif", marginBottom:2 }}>{docName(doc)}</div>
                      {docType(doc) && <div style={{ fontSize:11, color:"#9ca3af", fontFamily:"Arial, sans-serif", marginBottom: doc.analysis ? 6 : 0 }}>{docType(doc)}</div>}
                      {(() => {
                        // Parse analysis whether stored as object or JSON string
                        let a = doc.analysis;
                        if (typeof a === "string") { try { a = JSON.parse(a); } catch(e) { a = null; } }
                        if (!a) return null;
                        const findings = a.keyFindings || [];
                        const informationals = a.informationals || [];
                        return (
                          <div style={{ paddingLeft:8, borderLeft:"3px solid #e5e7eb" }}>
                            {findings.length > 0 && findings.map((f, fi) => (
                              <div key={fi} style={{ fontSize:12, color:"#374151", fontFamily:"Arial, sans-serif", lineHeight:1.6, marginBottom:2 }}>• {toStr(f)}</div>
                            ))}
                            {/* Material & Noted flag callouts intentionally not shown to the reviewer (aligns with the Jaybee report). */}
                            {/* Informationals — blue, factual context only */}
                            {informationals.length > 0 && (
                              <div style={{ marginTop:6, padding:"8px 10px", background:"#eff6ff", border:"1px solid #bfdbfe", borderRadius:4 }}>
                                <div style={{ fontSize:10, color:"#1e40af", fontWeight:700, textTransform:"uppercase", letterSpacing:"0.5px", fontFamily:"Arial, sans-serif", marginBottom:4 }}>ℹ️ Informational</div>
                                {informationals.map((f, fi) => (
                                  <div key={fi} style={{ fontSize:12, color:"#1e3a8a", fontFamily:"Arial, sans-serif", lineHeight:1.6 }}>• {toStr(f)}</div>
                                ))}
                              </div>
                            )}
                            {/* MLRO mitigation note — always shown when present, always retained in audit trail */}
                            {doc.mlro_note && (
                              <div style={{ marginTop:6, padding:"8px 10px", background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:4 }}>
                                <div style={{ fontSize:10, color:"#166534", fontWeight:700, textTransform:"uppercase", letterSpacing:"0.5px", fontFamily:"Arial, sans-serif", marginBottom:3 }}>✓ MLRO Mitigation Note</div>
                                <div style={{ fontSize:12, color:"#14532d", fontFamily:"Arial, sans-serif", lineHeight:1.5 }}>{doc.mlro_note}</div>
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                    <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:6, flexShrink:0 }}>
                      <span style={S.badge(doc.assessment === "Satisfactory" ? "#15803d" : doc.assessment === "Requires Clarification" ? "#d97706" : "#6b7280")}>
                        {doc.assessment || "Received"}
                      </span>
                      {((doc.file_path && doc.file_path !== 'pending') || doc.file_url) ? (
                        <button onClick={() => openFile(doc.file_path, doc.file_url)} style={{ fontSize:11, color:"#1e2d5a", background:"transparent", border:"1px solid #1e2d5a", borderRadius:4, padding:"3px 10px", cursor:"pointer", fontFamily:"Arial, sans-serif" }}>View</button>
                      ) : <span style={{ fontSize:11, color:"#d1d5db" }}>None</span>}
                      {/* Working English translation. Belgian NBB filings, Swiss German registers and
                          the like are unreadable to most external reviewers, who could previously open
                          a document and not read it. The ORIGINAL remains the record — the translation
                          is a working aid and says so. */}
                      {doc.id && (doc.file_path && doc.file_path !== 'pending') && (
                        <button onClick={() => openTranslation(doc)} style={{ fontSize:11, color:"#6b7280", background:"transparent", border:"1px solid #d1d5db", borderRadius:4, padding:"3px 10px", cursor:"pointer", fontFamily:"Arial, sans-serif" }}>English</button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 8. MLRO Sign-off statement */}
        <div style={S.section}>
          <div style={S.sectionTitle}>8. MLRO Internal Review &amp; Sign Off</div>
          <div style={{ padding:"14px 16px", background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:8 }}>
            <div style={{ fontSize:13, color:"#14532d", fontFamily:"Arial, sans-serif", lineHeight:1.7 }}>
              This file has been reviewed by the firm's internal MLRO. All risk factors identified during the onboarding process have been assessed, mitigated where appropriate, and signed off internally prior to submission for this review. The MLRO's conditions and full case notes are set out at the top of this file.
            </div>
            <div style={{ marginTop:10, fontSize:12, color:"#166534", fontFamily:"Arial, sans-serif" }}>
              Approved by: {session?.mlro_decision ? session.mlro_decision + ", " : ""}{session?.approved_at ? new Date(session.approved_at).toLocaleDateString("en-GB", { day:"numeric", month:"long", year:"numeric" }) : "date not recorded"}
            </div>
          </div>
        </div>

        {/* 9. Travel Rule Detail */}
        {report.travelRule?.wallets?.length > 0 && (
          <div style={S.section}>
            <div style={S.sectionTitle}>9. Travel Rule: Wallet Ownership Verification</div>
            <div style={{ ...S.para, color:"#6b7280", marginBottom:14 }}>
              The following table documents Travel Rule compliance for each virtual asset wallet associated with this client, as required under applicable Travel Rule guidance.
            </div>
            <table style={{ width:"100%", borderCollapse:"collapse", border:"1px solid #e5e7eb", borderRadius:8, overflow:"hidden" }}>
              <thead>
                <tr style={{ background:"#1e2d5a" }}>
                  {["Wallet Address","Type","Exchange / Method","Status"].map(h => (
                    <th key={h} style={{ padding:"8px 10px", textAlign:"left", fontSize:11, color:"white", fontFamily:"Arial, sans-serif", fontWeight:700 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {report.travelRule.wallets.map((w, i) => (
                  <tr key={i} style={{ background: i%2===0 ? "#fff" : "#f9fafb", borderBottom:"1px solid #f3f4f6" }}>
                    <td style={{ padding:"8px 10px", fontSize:11, color:"#374151", fontFamily:"monospace" }}>{w.address?.slice(0,16)}...{w.address?.slice(-6)}</td>
                    <td style={{ padding:"8px 10px", fontSize:12, color:"#374151", fontFamily:"Arial, sans-serif", textTransform:"capitalize" }}>{w.type || "Not given"}</td>
                    <td style={{ padding:"8px 10px", fontSize:12, color:"#374151", fontFamily:"Arial, sans-serif" }}>
                      {w.type === "exchange" ? `${w.exchangeName || "Exchange not named"} ${w.exchangeRegulated ? "· Regulated" : "· Unregulated"} ${w.accountInEntityName ? "· Account verified" : ""}` :
                       w.type === "private" ? `Proof of control: ${w.proofOfControl?.method === "satoshi_test_complete" ? `Satoshi test verified ${w.proofOfControl.verifiedDate ? new Date(w.proofOfControl.verifiedDate).toLocaleDateString("en-GB") : ""}` : "Pending"}` : "Not applicable"}
                    </td>
                    <td style={{ padding:"8px 10px" }}>
                      <span style={S.badge(w.travelRuleStatus === "compliant" ? "#15803d" : "#d97706")}>
                        {w.travelRuleStatus === "compliant" ? "✓ Compliant" : w.travelRuleStatus === "pending_satoshi_test" ? "Satoshi pending" : w.travelRuleStatus === "pending_client_response" ? "Awaiting client" : "Pending"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        </>)}

        {/* v1_847: the "Ask a Question About This File" panel is removed. It called
            /api/command-interface with no credential, and that route admits only a
            signed in member of the case's firm, so for the second signatory (and the MLRO's
            preview of this page) every question
            came back "Error: Not signed in". A control that can never work is
            hidden, not left to fail. */}

        {/* Decision Panel — signatory mode only */}
        {!isPreview && !shareMode && review && (
          <div style={{ ...S.section, border: "2px solid #13a4a4" }}>
            <div style={{ ...S.sectionTitle, color: "#0f766e", borderBottomColor: "#13a4a4" }}>Second Sign Off: Your Decision</div>
            <div style={{ ...S.para, marginBottom: 16 }}>
              Having reviewed the above client file prepared by your firm's compliance team, please record your decision below. Your decision will be immediately logged and the compliance team notified.
            </div>

            {caseNotes.length > 0 && (
              <button onClick={() => setShowNotes(true)} style={{ ...S.btn("#1e2d5a", "#fff"), width: "100%", marginBottom: 16, padding: "12px 16px" }}>
                View MLRO Case Notes ({caseNotes.length})
              </button>
            )}

            <div style={{ padding:"12px 16px", background:"#f0f9ff", border:"1px solid #bae6fd", borderRadius:8, marginBottom:20 }}>
              <div style={{ fontSize:12, color:"#0369a1", fontFamily:"Arial, sans-serif", lineHeight:1.7 }}>
                <strong>Please note:</strong> Recording your approval here does <strong>not</strong> automatically send a welcome email to the client. Your approval is confirmation of your sign off on this file. The compliance team sends the client their welcome email as a deliberate, separate step once all final checks are complete.
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", fontSize: 12, color: "#6b7280", fontFamily: "Arial, sans-serif", marginBottom: 6 }}>Notes / Conditions (required if returning the file)</label>
              <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Add any comments, conditions, or instructions for the compliance team..." style={{ width: "100%", padding: "10px 14px", border: "1px solid #d1d5db", borderRadius: 6, fontSize: 13, fontFamily: "Arial, sans-serif", resize: "vertical", minHeight: 80, outline: "none", boxSizing: "border-box" }} />
            </div>

            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <button onClick={() => submitDecision("approve")} disabled={submitting} style={{ ...S.btn("#13a4a4", "#fff"), opacity: submitting ? 0.6 : 1 }}>
                {submitting ? "Submitting..." : "✓ Approve file →"}
              </button>
              <button onClick={() => submitDecision("info")} disabled={submitting} style={{ ...S.btn("#d97706", "#fff"), opacity: submitting ? 0.6 : 1 }}>
                ← Return to compliance
              </button>
            </div>
          </div>
        )}

        {/* Preview note where the decision panel would sit */}
        {isPreview && (
          <div style={{ ...S.section, border: "1px dashed #13a4a4", background: "#f0fdfa" }}>
            <div style={{ fontSize: 12, color: "#0f766e", fontFamily: "Arial, sans-serif", lineHeight: 1.7 }}>
              <strong>Preview mode.</strong> This is the report exactly as the second signatory sees it. In the signatory's copy, a decision panel appears here with Approve file and Return to compliance actions, plus access to the MLRO case notes.
              {caseNotes.length > 0 && (
                <button onClick={() => setShowNotes(true)} style={{ display: "block", marginTop: 10, padding: "8px 14px", background: "#1e2d5a", color: "#fff", border: "none", borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "Arial, sans-serif" }}>
                  View MLRO Case Notes ({caseNotes.length})
                </button>
              )}
            </div>
          </div>
        )}

        {/* Case notes popup */}
        {showNotes && (
          <div onClick={() => setShowNotes(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 }}>
            <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, maxWidth: 760, width: "100%", maxHeight: "85vh", overflowY: "auto", padding: "22px 26px", boxShadow: "0 12px 48px rgba(0,0,0,0.35)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, paddingBottom: 10, borderBottom: "2px solid #1e2d5a" }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#1e2d5a", fontFamily: "Arial, sans-serif", textTransform: "uppercase", letterSpacing: "0.6px" }}>MLRO Case Notes ({caseNotes.length})</div>
                <button onClick={() => setShowNotes(false)} style={{ fontSize: 24, lineHeight: 1, color: "#6b7280", background: "transparent", border: "none", cursor: "pointer", fontFamily: "Arial, sans-serif" }}>×</button>
              </div>
              {caseNotes.map((n, i) => (
                <div key={i} style={{ padding: "10px 14px", background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 6, marginBottom: 8 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: "#1e2d5a", fontFamily: "Arial, sans-serif" }}>{toStr(n.author) || "MLRO"}</span>
                    <span style={{ fontSize: 11, color: "#9ca3af", fontFamily: "Arial, sans-serif" }}>{n.created_at ? new Date(n.created_at).toLocaleString("en-GB") : ""}</span>
                  </div>
                  <div style={{ fontSize: 13, color: "#374151", fontFamily: "Arial, sans-serif" }}>{formatNotes(toStr(n.content))}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Client interview transcript. Every substantive conclusion in this report rests on what
            the client said, and the reviewer previously saw only the MLRO's characterisation of it.
            Collapsed by default so it does not dominate the document, modelled on the case notes
            section. */}
        {transcript.length > 0 && (
          <div style={S.section}>
            <div onClick={() => setShowTranscript(v => !v)} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", userSelect: "none" }}>
              <div style={S.sectionTitle}>Client Interview Transcript ({transcript.length} messages)</div>
              <span style={{ fontSize: 16, color: "#9ca3af" }}>{showTranscript ? "▲" : "▼"}</span>
            </div>
            <div style={{ fontSize: 11, color: "#6b7280", fontFamily: "Arial, sans-serif", marginTop: 4 }}>
              The complete compliance interview, as conducted. This is the primary evidence for the client's own statements (source of wealth, business model, expected activity and authority) and can be read against the findings above.
            </div>
            {showTranscript && (
              <div style={{ marginTop: 12 }}>
                {transcript.map((m, i) => {
                  const isClient = m.role === "user";
                  return (
                    <div key={i} style={{ marginBottom: 10, paddingLeft: isClient ? 0 : 0 }}>
                      <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.5px", fontWeight: 700, color: isClient ? "#1e2d5a" : "#9ca3af", fontFamily: "Arial, sans-serif", marginBottom: 3 }}>
                        {isClient ? "Client" : "Alex (AI interviewer)"}
                        {m.created_at ? <span style={{ fontWeight: 400, letterSpacing: 0, textTransform: "none", color: "#9ca3af", marginLeft: 8 }}>{new Date(m.created_at).toLocaleString("en-GB")}</span> : null}
                      </div>
                      <div style={{ fontSize: 12, color: isClient ? "#111827" : "#4b5563", fontFamily: "Arial, sans-serif", lineHeight: 1.7, background: isClient ? "#f9fafb" : "transparent", border: isClient ? "1px solid #e5e7eb" : "none", borderRadius: isClient ? 6 : 0, padding: isClient ? "8px 12px" : "0 0 0 2px", whiteSpace: "pre-wrap" }}>
                        {toStr(m.content)}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Document translation viewer */}
        {translationView && (
          <div onClick={() => setTranslationView(null)} style={{ position: "fixed", inset: 0, background: "rgba(17,24,39,0.55)", display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "40px 20px", zIndex: 1000, overflowY: "auto" }}>
            <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 10, maxWidth: 820, width: "100%", padding: "24px 28px", fontFamily: "Arial, sans-serif", boxShadow: "0 10px 40px rgba(0,0,0,0.25)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: "#111827" }}>English translation</div>
                  <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>{docName(translationView.doc)}</div>
                </div>
                <button onClick={() => setTranslationView(null)} style={{ background: "transparent", border: "none", fontSize: 20, color: "#9ca3af", cursor: "pointer", lineHeight: 1 }}>×</button>
              </div>

              <div style={{ fontSize: 11, color: "#92400e", background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 6, padding: "8px 12px", marginBottom: 14, lineHeight: 1.6 }}>
                This is a working translation to aid review. The document in its original language remains the record and should be cited in any rationale.
              </div>

              {translationView.loading && <div style={{ fontSize: 13, color: "#6b7280", padding: "20px 0" }}>Translating: this can take up to a minute for a long document…</div>}
              {translationView.error && (
                <div style={{ fontSize: 12, color: "#b45309", background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 6, padding: "10px 12px" }}>
                  Could not produce a translation: {toStr(translationView.error)}
                </div>
              )}
              {translationView.translation && (
                <div>
                  {translationView.translation.sourceLanguage && (
                    <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 8 }}>
                      Original language: {toStr(translationView.translation.sourceLanguage)}{translationView.translation.truncated ? " · Long document, translation may be truncated" : ""}{translationView.cached ? " · Previously translated" : ""}
                    </div>
                  )}
                  <div style={{ fontSize: 12, color: "#111827", lineHeight: 1.8, whiteSpace: "pre-wrap", maxHeight: "60vh", overflowY: "auto", background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 6, padding: "14px 16px" }}>
                    {toStr(translationView.translation.text)}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Form A/K declaration viewer — same content the MLRO sees in the dashboard. */}
        {formView && (
          <div onClick={() => setFormView(null)} style={{ position: "fixed", inset: 0, background: "rgba(17,24,39,0.55)", display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "40px 20px", zIndex: 1000, overflowY: "auto" }}>
            <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 10, maxWidth: 760, width: "100%", padding: "24px 28px", fontFamily: "Arial, sans-serif", boxShadow: "0 10px 40px rgba(0,0,0,0.25)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: "#111827" }}>{formView.model?.title || `Form ${toStr(formView.row?.form)}`}</div>
                  <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>Declared by {toStr(formView.row?.signer)}{formView.row?.when ? " · " + new Date(formView.row.when).toLocaleString("en-GB") : ""}</div>
                </div>
                <button onClick={() => setFormView(null)} style={{ background: "transparent", border: "none", fontSize: 20, color: "#9ca3af", cursor: "pointer", lineHeight: 1 }}>×</button>
              </div>

              {formView.loading && <div style={{ fontSize: 13, color: "#6b7280", padding: "20px 0" }}>Loading the declaration…</div>}
              {formView.error && (
                <div style={{ fontSize: 12, color: "#b45309", background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 6, padding: "10px 12px" }}>
                  Could not load the declaration: {toStr(formView.error)}
                </div>
              )}

              {formView.model && (
                <div>
                  <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.5px", color: "#9ca3af", fontWeight: 700, marginBottom: 6 }}>Contracting Partner</div>
                  <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 8, padding: "10px 14px", marginBottom: 16, fontSize: 12, color: "#374151", lineHeight: 1.8 }}>
                    <div><span style={{ color: "#9ca3af" }}>Legal name: </span>{formView.model.contractingPartner.legalName || "Not given"}</div>
                    <div><span style={{ color: "#9ca3af" }}>Registered address: </span>{formView.model.contractingPartner.address || "Not given"}</div>
                    <div><span style={{ color: "#9ca3af" }}>Country: </span>{formView.model.contractingPartner.country || "Not given"}{formView.model.contractingPartner.registrationNumber ? " · Reg " + formView.model.contractingPartner.registrationNumber : ""}</div>
                  </div>

                  {formView.model.formType === "K" && formView.model.fiduciaryYes && (
                    <div style={{ background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 8, padding: "10px 12px", marginBottom: 16, fontSize: 12, color: "#92400e" }}>
                      Fiduciary holding of assets was declared on this Form K, which means a Form A is required from the beneficial owner who is a third party (fiduciary exception). Whether that has been actioned is recorded on the case by the MLRO.
                    </div>
                  )}

                  <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.5px", color: "#9ca3af", fontWeight: 700, marginBottom: 6 }}>Declared Content</div>
                  {(formView.model.fields || []).length === 0 && <div style={{ fontSize: 12, color: "#6b7280" }}>No questionnaire fields were returned for this declaration.</div>}
                  {(formView.model.fields || []).map((f, i) => (
                    <div key={f.id || i} style={{ display: "flex", gap: 12, padding: "7px 0", borderBottom: "1px solid #f3f4f6", fontSize: 12 }}>
                      <div style={{ width: 240, flexShrink: 0, color: "#6b7280" }}>{toStr(f.label)}</div>
                      <div style={{ color: "#111827", fontWeight: 500 }}>{toStr(f.value)}</div>
                    </div>
                  ))}

                  <div style={{ marginTop: 16, fontSize: 11, color: "#6b7280", background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 8, padding: "10px 14px", lineHeight: 1.8 }}>
                    <div>Confirmed: {formView.model.confirmation?.at ? new Date(formView.model.confirmation.at).toLocaleString("en-GB") : "Not recorded"}</div>
                    {formView.model.confirmation?.ip && <div>Confirmation IP: {toStr(formView.model.confirmation.ip)}</div>}
                    <div>Applicant action ID: {toStr(formView.model.confirmation?.actionId) || "Not recorded"}</div>
                    {(() => {
                      const phone = (formView.checks || []).find(c => /PHONE|SMS|OTP/i.test(c.checkType || ""));
                      return <div>Phone OTP verification: {phone ? (phone.answer === "GREEN" ? "Confirmed" : String(phone.answer)) : "Confirmed as part of this action's combined approval"}</div>;
                    })()}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        <div style={{ textAlign: "center", padding: "16px", fontFamily: "Arial, sans-serif", fontSize: 11, color: "#9ca3af" }}>
          Confidential · Decision Review Report · Generated by CompliantID · All onboarding decisions rest with the firm and its MLRO · {new Date().getFullYear()}
        </div>
      </div>
    </div>
  );
}
